import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavBar } from '../kuroco/AppBars';
import { AiBubble, UserBubble } from '../kuroco/ChatPrimitives';
import { ChatBanner, ChatInput } from '../kuroco/ChatChrome';
import { EditToast } from '../kuroco/EditHelpers';
import { fmt, EM } from '../kuroco/fmt';
import {
  FOCUS_ICON,
  fmtMetric,
  ALT_ACTIONS,
} from '../kuroco/goalEditUtils';
import { useSettingsData } from './useSettingsData';
import { useUserRole } from '../../hooks/useUserRole';
import { useNavigateWithFilters } from '../../utils/dashboardNav';
import { jstMonth } from '../../utils/jstDate';
import {
  MAX_PICK_MONTHS,
  formatGoalDateLabel,
  formatMonthLabel,
  lastDayOfMonth,
  monthKeyOffset,
  pickablePlanEndMonths,
  planLengthBetween,
  resolvePlanPeriod,
  resolvePlanStartMonth,
} from '../../utils/planPeriod';
import {
  hasUsablePosSnapshot,
  resolveNewReturnSnapshot,
  resolveSalesSnapshot,
  resolveUnitPriceSnapshot,
  STANCE_LABEL,
} from '../../utils/goalPropose';
import { buildGoalsConsultSummary, resolveGoalBaseSnapshot } from '../../utils/kpiSnapshot';
import {
  FUNNEL_LABEL,
  SKIP_OPTIONAL_TARGET,
  funnelStepView,
  nextFunnelStep,
  resumeStepFrom,
  setupStepAfter,
} from '../../utils/setupFunnel';
import {
  FOCUS_KPI_DEFS,
  FOCUS_LEVER_KEYS,
  FOCUS_SLOTS,
  SUB_KGI_KEYS,
  UNSELECTED_FOCUS,
  chartKeyForFocus,
  dropFocusKey,
  focusLeverKeysFor,
  formatFocusValue,
  formatGapCandidatesLine,
  getFocusDef,
  readFocusActual,
  resolveFocusSelection,
  resolveSubKgi,
} from '../../utils/kpiFocusCatalog';
import {
  KPI_IDENTITY_CHEATSHEET,
  buildAutoProposePrompt,
  mapProposalToCardFields,
  validateGoalProposalTargets,
} from '../../utils/goalSettingsAi';
import { createChatSession, postChatMessageStream } from '../../api/dashboardApi';
import { confirmProposedActions } from '../../api/goalsApi';
import { CADENCES } from '../../constants/cadences';
import {
  MAX_PLAN_ACTIONS,
  actionTargetKeys,
  actionTargetLabel,
  actionsRequestText,
  fallbackActionsFor,
  groupActionsByTarget,
} from '../../utils/actionTargets';
import { notifyGoalsUpdated } from '../../utils/goalsLocalStore';

const VALID_EDIT = new Set(['goal', 'subkgi', 'period', 'kpi1', 'kpi2', 'kpi3', 'kpi', 'actions']);

/** Scopes that use the goal_setup screen (steppers, no AI proposal): 売上 KGI / 追加KGI / 期間. */
const isGoalSetupScope = (s) => s === 'goal' || s === 'subkgi' || s === 'period';

/** ?edit=kpiN → slot N (1–3); anything else → null. */
const SLOT_OF_SCOPE = { kpi1: 1, kpi2: 2, kpi3: 3 };
const slotLabel = (n) => FOCUS_SLOTS[n - 1]?.label || '';
const isSlotScope = (s) => Boolean(SLOT_OF_SCOPE[s]);

/** Local calendar YYYY-MM-DD — never toISOString (UTC can shift the day/month in JST). */
const TREND_BY_FOCUS = {
  newReturn: { field: 'new_return_rate', unit: 'percent' },
  nomination: { field: 'nomination_rate', unit: 'percent' },
  retailSales: { field: 'retail_sales', unit: 'currency' },
  totalSales: { field: 'total_sales', unit: 'currency' },
  customerCount: { field: 'customer_count', unit: 'count' },
  unitPrice: { field: 'unit_price', unit: 'currency' },
};

/**
 * Scope line for 【目標相談】 — AI must only propose that slice.
 * @param metrics [card①, card②, card③] (draft)
 * @param subKgiKey draft 追加KGI key — a KGI is never a 注力
 * @param slots   for scope 'kpi': the slots being proposed
 */
function scopeTargetLine(scope, metrics = [], subKgiKey = null, slots = []) {
  if (scope === 'goal') return '対象: 最終ゴール（単月売上）のみ / 他の項目は変更しない';
  if (scope === 'subkgi') return '対象: 追加KGIのみ（画面で入力） / 他の項目は変更しない';
  if (scope === 'period') return '対象: 目標期間のみ / 他の項目は変更しない';
  const kgiLine =
    `KGIの指標（totalSales${subKgiKey ? ` と 追加KGIの ${subKgiKey}` : ''}）は注力に選ばない。`;
  // Each scoped line names the keys the OTHER slots already hold and forbids them.
  // Without this the model re-proposed the same KPI for ②, and the card then
  // rendered 未選択 with a live target nobody could accept.
  const n = SLOT_OF_SCOPE[scope];
  if (n) {
    const s = FOCUS_SLOTS[n - 1];
    const m = metrics[n - 1];
    const taken = FOCUS_SLOTS.filter((o) => o.slot !== n && metrics[o.slot - 1]?.isSelected).map(
      (o) => `注力${o.label}が key=${metrics[o.slot - 1].key} を使用中`
    );
    return (
      `対象: 注力指標${s.label}のみ（GOAL_PROPOSAL は ${s.keyField}+${s.targetField}）。` +
      `KPIの種類を変えるときは ${s.keyField} を必ず入れる（例: nomination）。` +
      (m?.isSelected
        ? `現在の${s.label}は ${m.label}（key=${m.key}）。`
        : `現在の${s.label}は未選択。GAP候補のkeyから1つ選び、${s.keyField} を必ず入れる。`) +
      (taken.length ? `${taken.join('・')}。これらのkeyは ${s.keyField} に絶対に選ばない（同じ指標は2つの枠に置けない）。` : '') +
      kgiLine +
      '他の注力 / finalSales / planLength は変更しない'
    );
  }
  if (scope === 'kpi') {
    const labels = (slots.length ? slots : [1, 2]).map(slotLabel).join('');
    return `対象: 注力指標${labels}のみ / ${kgiLine}他の項目は変更しない`;
  }
  if (scope === 'actions') return '対象: 行動プランのみ / 他の項目は変更しない';
  return null;
}

function scopeIntroHtml(scope) {
  if (scope === 'goal') {
    return 'いま編集しているのは<strong>最終ゴール（単月売上）</strong>のみです。他の項目は変更しません。';
  }
  if (scope === 'subkgi') {
    return 'いま編集しているのは<strong>追加KGI</strong>のみです。他の項目は変更しません。';
  }
  if (scope === 'period') {
    return 'いま編集しているのは<strong>目標期間</strong>のみです。他の項目は変更しません。';
  }
  if (isSlotScope(scope)) {
    const n = SLOT_OF_SCOPE[scope];
    return (
      `いま編集しているのは<strong>注力指標${slotLabel(n)}</strong>のみです（指標の種類も変えられます` +
      `${n > 1 ? '。この枠は任意なので外すこともできます' : ''}）。他の項目は変更しません。`
    );
  }
  if (scope === 'kpi') {
    return 'いま編集しているのは<strong>注力指標</strong>のみです。他の項目は変更しません。';
  }
  if (scope === 'actions') {
    return 'いま編集しているのは<strong>行動プラン</strong>のみです。他の項目は変更しません。';
  }
  return '目標・行動プランの変更画面です。<br>どの項目を変更しますか？';
}

function isScopedSlice(scope) {
  return isGoalSetupScope(scope) || isSlotScope(scope) || scope === 'kpi' || scope === 'actions';
}

function unitPriceFromRow(r) {
  if (!r || r.total_sales == null) return null;
  const cust =
    typeof r.customer_count === 'number'
      ? r.customer_count
      : (Number(r.new_customer_count) || 0) + (Number(r.repeat_customer_count) || 0);
  if (!cust) return null;
  return Math.round(Number(r.total_sales) / cust);
}

function buildTrendDetail(kpiHistory, field, unit) {
  if (!Array.isArray(kpiHistory) || kpiHistory.length === 0) return null;
  const vals = kpiHistory
    .slice(-3)
    .map((r) => {
      if (field === 'new_return_rate' || field === 'new_customer_return_rate') {
        return r.new_return_rate ?? r.new_customer_return_rate;
      }
      if (field === 'unit_price') return unitPriceFromRow(r);
      return r[field];
    })
    .filter((v) => v != null && !Number.isNaN(v));
  if (vals.length === 0) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (unit === 'percent') {
    return `直近${vals.length}ヶ月で ${min.toFixed(1)}% 〜 ${max.toFixed(1)}% で推移しています。`;
  }
  if (unit === 'currency') {
    return `直近${vals.length}ヶ月で ${fmt('currency', min)} 〜 ${fmt('currency', max)} で推移しています。`;
  }
  return `直近${vals.length}ヶ月のPOSデータを分析しました。`;
}

/**
 * Metric card for a saved focus key (goals.focusKey / subFocusKey).
 * Must follow goals — no 新規再来率 / 客単価 default — so an unpicked slot stays 未選択
 * instead of proposing a target for a KPI the user never chose.
 */
function buildFocusMetricCard(focusKey, slotLabel, kpis, kpiHistory, selectedMonth) {
  const def = getFocusDef(focusKey);
  if (!def) {
    return {
      key: null,
      impact: slotLabel === '①' ? 'high' : 'mid',
      headline: `注力指標${slotLabel} — 未選択`,
      detail: 'まず注力指標を選んでください。ホームPOSにある指標から選べます。',
      current: null,
      unit: null,
      label: UNSELECTED_FOCUS.label,
      value: EM,
      sourceMonth: selectedMonth,
      fromHistory: false,
      isSelected: false,
    };
  }
  const key = def.key;
  let current = readFocusActual(key, kpis, kpiHistory, selectedMonth);
  let sourceMonth = selectedMonth;
  let fromHistory = false;

  // Prefer dedicated snapshots when available (same numbers as hybrid propose)
  if (key === 'newReturn') {
    const nrr = resolveNewReturnSnapshot(kpis, kpiHistory, selectedMonth);
    if (nrr) {
      current = nrr.value;
      sourceMonth = nrr.month || selectedMonth;
      fromHistory = !!nrr.fromHistory;
    }
  } else if (key === 'unitPrice') {
    const up = resolveUnitPriceSnapshot(kpis, kpiHistory, selectedMonth);
    if (up) {
      current = up.value;
      sourceMonth = up.month || selectedMonth;
      fromHistory = !!up.fromHistory;
    }
  }

  const trendMeta = TREND_BY_FOCUS[key];
  const detail =
    (trendMeta && buildTrendDetail(kpiHistory, trendMeta.field, trendMeta.unit)) ||
    'ホームPOSスナップショットをもとに改善余地を検討します。';

  return {
    key,
    impact: slotLabel === '①' ? 'high' : 'mid',
    headline:
      current != null
        ? `${def.label} ${fmtMetric(def.unit, current)} — 注力指標${slotLabel}`
        : `${def.label} — 注力指標${slotLabel}`,
    detail,
    current,
    unit: def.unit,
    label: def.label,
    value: current != null ? fmtMetric(def.unit, current) : EM,
    sourceMonth,
    fromHistory,
    isSelected: true,
  };
}

function isGoalsConfirmChip(text) {
  const t = (text || '').trim();
  return t.includes('この目標で進める') || t.includes('この目標で保存');
}

/**
 * `editor` (optional): a stepper/input for 改善目標 rendered in place of the static number.
 * The AI proposes; the user can type over it on the same card and「この案で進める」saves the typed value.
 */
function MetricCard({ metric, target, why, editor = null }) {
  if (!metric) return null;
  return (
    <div className="botl-evidence" style={{ marginBottom: 10 }}>
      <div className="botl-evidence-h">
        <span className={`botl-badge botl-badge--${metric.impact}`}>
          影響度 {metric.impact === 'high' ? '高' : '中'}
        </span>
        <span className="botl-evidence-name">
          {FOCUS_ICON[metric.key] || '📊'} {metric.label}
        </span>
      </div>
      <div className="botl-evidence-row">
        <span className="botl-evidence-k">現状</span>
        <span className="botl-evidence-v">{metric.value}</span>
      </div>
      <div className="botl-evidence-detail">{metric.detail}</div>
      {/* Never show a 改善目標 on an unselected card: with no KPI the number has
          no unit and no meaning, and 「この案で進める」 stays disabled anyway. */}
      {metric.isSelected && (target != null || why) && (
        <div className="botl-target" style={{ marginTop: 8 }}>
          {/* 現状 is already the row above — repeating it here printed the same
              value twice in a row inside one card. */}
          <div className="botl-target-arrow">↓ 根拠付き推奨（AI）</div>
          <div className="botl-target-row botl-target-row--goal">
            <span className="botl-target-k">改善目標</span>
            {editor && target != null ? (
              editor
            ) : (
              <span className="botl-target-v botl-target-v--goal">
                {target != null ? fmtMetric(metric.unit, target) : '—'}
              </span>
            )}
          </div>
          {why ? <div className="botl-evidence-detail" style={{ marginTop: 6 }}>{why}</div> : null}
        </div>
      )}
    </div>
  );
}

export default function GoalEditScreen() {
  const navigate = useNavigateWithFilters();
  const [searchParams] = useSearchParams();
  const editFromUrl = searchParams.get('edit');
  const initialScope = VALID_EDIT.has(editFromUrl) ? editFromUrl : null;
  // Mirrors _SELF_DECIDING_ROLES in app/api/routes/goals.py: nobody approves a manager's own
  // plan, the backend 確定s it on submit. dev is excluded there too (dev tests the real flow).
  const { role } = useUserRole();
  const selfDecides = role === 'manager' || role === 'beauty_manager' || role === 'company_admin';
  const {
    D,
    goals,
    setGoals,
    goalsLoading,
    canEditGoals,
    appendChangeLogApi,
    submitActions,
    kpis,
    kpiHistory,
    mlForecasts,
    loading,
    selectedStore,
    selectedStylist,
    selectedMonth,
  } = useSettingsData();
  const gt = D.goalTrack;
  const yen = (v) => fmt('currency', v);
  const filtersReady = Boolean(selectedStore && selectedStylist && selectedMonth);
  const posReady = hasUsablePosSnapshot(kpis, kpiHistory, loading);
  const waitingForFirstSnap = Boolean(
    filtersReady && !posReady && (loading?.kpis || loading?.kpiHistory)
  );

  // First-run sequence (初期登録) step, or null outside it — see the comment at the phase state.
  const [funnelStep, setFunnelStep] = useState(null);

  // Which month the screen's 実績 come from. In the 初期登録 flow a month still running is only
  // MTD, so the picker chips, 現状, the proposal floor and the gap ranking all read the last
  // complete month (resolveGoalBaseSnapshot derives it from today's JST date — never fixed).
  // Outside the first-run flow the selected month stays the base, as before.
  const base = useMemo(
    () =>
      funnelStep
        ? resolveGoalBaseSnapshot(kpis, kpiHistory, selectedMonth)
        : { kpis: kpis ?? null, month: selectedMonth ?? null, label: '現状', mtd: false },
    [funnelStep, kpis, kpiHistory, selectedMonth]
  );
  const baseKpis = base.kpis;
  const baseMonth = base.month;
  const baseLabel = base.label;

  // Local drafts per 注力 slot ①②③ (index 0–2) — AI / 手動編集 change them before save
  // (goals updated on save/confirm). 注力 has no default: null until the user (or a confirmed AI
  // proposal) picks one, and null again when a slot is removed — a removed slot must not fall
  // back to the saved key (TASK 5章の1).
  const savedSelection = resolveFocusSelection(goals);
  const savedKeys = FOCUS_SLOTS.map((s) => savedSelection[s.keyField]);
  const savedTargets = FOCUS_SLOTS.map((s) => goals?.[s.targetField] ?? null);
  const [draftKeys, setDraftKeys] = useState(savedKeys);
  const [targets, setTargets] = useState(savedTargets);
  const [whys, setWhys] = useState(['', '', '']);
  const setDraftKey = (n, key) => setDraftKeys((p) => p.map((k, i) => (i === n - 1 ? key : k)));
  const setTarget = (n, v) => setTargets((p) => p.map((t, i) => (i === n - 1 ? v : t)));
  const setWhy = (n, v) => setWhys((p) => p.map((w, i) => (i === n - 1 ? v : w)));
  const clearWhys = () => setWhys(['', '', '']);

  // 追加KGI draft (KGI step). Entered by hand — the AI never proposes it.
  const savedSubKgi = resolveSubKgi(goals);
  const [subKgiKey, setSubKgiKey] = useState(savedSubKgi?.key ?? null);
  const [subKgiTarget, setSubKgiTarget] = useState(savedSubKgi?.target ?? null);

  // Follow the server: when the saved slots change (save / confirm / late load), the drafts
  // follow — null included, so a slot cleared on the server does not linger on screen.
  const savedKeySig = savedKeys.join('|');
  const savedTargetSig = savedTargets.join('|');
  useEffect(() => {
    setDraftKeys(savedKeys);
    setTargets(savedTargets);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the value signatures
  }, [savedKeySig, savedTargetSig]);
  useEffect(() => {
    setSubKgiKey(savedSubKgi?.key ?? null);
    setSubKgiTarget(savedSubKgi?.target ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the saved values
  }, [savedSubKgi?.key, savedSubKgi?.target]);

  // One card per slot, following the drafts — no NRR+客単価 default. A key repeating an earlier
  // slot, or equal to the 追加KGI, stays 未選択 rather than swapping in another KPI.
  const metrics = useMemo(() => {
    const seen = [];
    return FOCUS_SLOTS.map((s, i) => {
      const k = draftKeys[i];
      const ok = Boolean(k) && !seen.includes(k) && k !== subKgiKey;
      if (ok) seen.push(k);
      return buildFocusMetricCard(ok ? k : null, s.label, baseKpis, kpiHistory, baseMonth);
    });
  }, [draftKeys, subKgiKey, baseKpis, kpiHistory, baseMonth]);
  const metricOf = (n) => metrics[n - 1];
  // KGI keys against the DRAFT 追加KGI: the picker must grey out what is being chosen now,
  // not only what was saved (TASK 5章の3)
  const kgiKeys = ['totalSales', ...(subKgiKey ? [subKgiKey] : [])];
  const selectedSlots = FOCUS_SLOTS.filter((s) => metricOf(s.slot)?.isSelected).map((s) => s.slot);
  // 行動プランの指標 in importance order (追加KGI → 注力①②③), from what is on screen now
  const actionKeys = actionTargetKeys(
    subKgiKey,
    metrics.map((m) => (m.isSelected ? m.key : null))
  );

  // The sales default is the last COMPLETE month in every flow (owner 2026-10-23): a running
  // month is MTD and reads as a too-low goal (¥763,360 on the 22nd). `base` above keeps the
  // selected month outside the first-run flow for the 注力 chips; only the KGI seed differs.
  const salesBase = useMemo(
    () => resolveGoalBaseSnapshot(kpis, kpiHistory, selectedMonth),
    [kpis, kpiHistory, selectedMonth]
  );
  const salesSnap = resolveSalesSnapshot(salesBase.kpis, kpiHistory, salesBase.month);
  const baseSales = gt.finalSales ?? salesSnap?.value ?? 500000;

  // Declared before the deadline memo (which needs it) — same expression as hasSavedGoal below
  const hasSavedGoalRef = useRef(false);
  hasSavedGoalRef.current =
    (goals?.salesTarget ?? goals?.finalSales ?? null) != null &&
    (goals?.planLength != null || goals?.goalDate != null || goals?.planStartMonth != null);
  const [sales, setSales] = useState(goals?.finalSales ?? baseSales);
  // kpiHistory arrives after the first render, so the seed above may still be the MTD value.
  // Follow the default while the person has not touched it and nothing is saved.
  const lastSalesDefault = useRef(baseSales);
  useEffect(() => {
    if (goals?.finalSales != null) return;
    if (sales === lastSalesDefault.current && baseSales !== sales) setSales(baseSales);
    lastSalesDefault.current = baseSales;
  }, [baseSales, goals?.finalSales, sales]);
  const [months, setMonths] = useState(goals?.planLength ?? 6);
  const [toast, setToast] = useState(false);
  const [toastText, setToastText] = useState('目標設定を保存しました');
  // The plan period is ONE calculation everywhere (utils/planPeriod): start month + length.
  // An existing goal keeps its start month; a goal set from scratch starts this JST month.
  // The screen picks START + END months (齋藤さん 2026-10-16); `months` stays the stored
  // planLength so AI proposals / API answers (setMonths) and every「Nヶ月」reader keep working.
  const savedStartMonth = useMemo(
    () => (hasSavedGoalRef.current ? resolvePlanStartMonth(goals) : null) ?? jstMonth(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goals?.planStartMonth, goals?.goalDate, goals?.planLength, goals?.createdAt]
  );
  const [planStartMonth, setPlanStartMonth] = useState(savedStartMonth);
  useEffect(() => setPlanStartMonth(savedStartMonth), [savedStartMonth]);
  const thisMonth = jstMonth();
  const endMonthOptions = useMemo(
    () => pickablePlanEndMonths(planStartMonth, thisMonth),
    [planStartMonth, thisMonth]
  );
  // 開始月 choices: last month (a plan that already began) … 12 months ahead. The END month is
  // what may run far out (MAX_PICK_MONTHS); a start more than a year away is not a plan yet.
  const startMonthOptions = useMemo(
    () => Array.from({ length: 12 + 1 }, (_, i) => monthKeyOffset(thisMonth, i - 1)),
    [thisMonth]
  );
  const planEndMonth = monthKeyOffset(planStartMonth, Math.max(1, months) - 1);
  // An end month the picker could not offer (saved 24ヶ月 plan, deadline already passed,
  // AI proposal beyond the cap) is shown as is; 次へ is disabled until a listed one is picked.
  const endMonthPickable = endMonthOptions.includes(planEndMonth);
  const pickStartMonth = (mk) => {
    setPlanStartMonth(mk);
    // Keep the end month where it was when still allowed, else the nearest allowed one.
    const opts = pickablePlanEndMonths(mk, thisMonth);
    if (opts.length === 0) return;
    const keep = opts.includes(planEndMonth) ? planEndMonth : opts[0];
    setMonths(planLengthBetween(mk, keep));
  };
  const pickEndMonth = (mk) => {
    const len = planLengthBetween(planStartMonth, mk);
    if (len) setMonths(len);
  };
  const deadlineYmd = useMemo(() => lastDayOfMonth(planEndMonth), [planEndMonth]);
  const deadlineLabel = useMemo(() => formatMonthLabel(planEndMonth) ?? EM, [planEndMonth]);
  // 「〜2027年3月（6ヶ月）」— the one way the period is written on this screen
  const periodLabel = `〜${deadlineLabel}（${months}ヶ月）`;

  // First run — no KGI and no period saved yet. Then the 変更内容の選択 menu is the wrong
  // entry point: there is nothing to choose between, every item is empty. Drop straight into
  // the funnel instead (KGI+期間 → 注力 → 行動プラン), which decideGoal already chains when
  // scope is null. Once a goal exists we go back to per-item editing, because running the
  // whole funnel to change one number is the annoying case.
  const hasSavedGoal =
    (goals?.salesTarget ?? goals?.finalSales ?? null) != null &&
    (goals?.planLength != null || goals?.goalDate != null);
  // First run runs as one guided sequence: KGI+期間 → 注力① → 注力② → 行動プラン, each step
  // saved as it is agreed. null once the sequence is done, or when a goal already exists and
  // the user is editing a single item.
  //
  // It is NOT decided here. goals start from localStorage and are replaced by the API answer,
  // so on a cold browser they read as empty for the first renders — deciding now would drop a
  // stylist who has a goal into the first-run sequence. The effect below switches into it once
  // goalsLoading has cleared and the value is actually known.
  // (funnelStep state is declared next to the base snapshot above)

  const [scope, setScope] = useState(initialScope);
  const [phase, setPhase] = useState(() => {
    if (isGoalSetupScope(initialScope)) return 'goal_setup';
    if (isSlotScope(initialScope) || initialScope === 'kpi') return 'analyzing';
    if (initialScope === 'actions') return 'actions_proposed';
    return 'choose';
  });
  const [editTarget, setEditTarget] = useState(() => {
    if (isGoalSetupScope(initialScope)) return 'goal';
    if (isSlotScope(initialScope) || initialScope === 'kpi') return 'metric';
    if (initialScope === 'actions') return 'actions';
    return null;
  });
  // Slot the user chose to remove (②③ only) — saved as explicit nulls on「保存する」
  const [removeSlot, setRemoveSlot] = useState(null);
  // 「指名率をKGIにしたので、注力①から外しました」— set when the KGI step drops a 注力
  const [droppedNote, setDroppedNote] = useState('');
  // Empty, never the canned list: a placeholder plan rendered before the model answers reads
  // as a real proposal, and the three template lines were being shown as if AI wrote them.
  const [actions, setActions] = useState(() => (goals?.actions?.length ? goals.actions : []));
  const [decided, setDecided] = useState(() =>
    initialScope && !isGoalSetupScope(initialScope)
      ? {
          sales: goals?.finalSales ?? baseSales,
          months: goals?.planLength ?? 6,
          deadlineLabel: (() => {
            const start = resolvePlanStartMonth(goals) ?? jstMonth();
            const end = monthKeyOffset(start, (goals?.planLength ?? 6) - 1);
            const m = /^(\d{4})-(\d{2})$/.exec(end || '');
            return m ? `${Number(m[1])}年${Number(m[2])}月` : EM;
          })(),
        }
      : null
  );
  const [chatLog, setChatLog] = useState([]);
  const [aiTyping, setAiTyping] = useState(false);
  // 再計算 cycles 標準 → 強気 → 慎重 (passed into AI auto-prompt)
  const [stance, setStance] = useState('standard');
  const [stanceNote, setStanceNote] = useState('');
  const [proposalSource, setProposalSource] = useState(null); // 'ai' | 'manual' | null
  // Inline manual action editing — keeps the user inside this chat (no route change)
  const [manualEditing, setManualEditing] = useState(false);
  const [manualActs, setManualActs] = useState([]);
  // Manual KPI picker on metric_proposed (catalog = FOCUS_LEVER_KEYS, KGI keys greyed out)
  const [manualMetricEditing, setManualMetricEditing] = useState(false);
  const [manualMetricSlot, setManualMetricSlot] = useState(1); // 1 = ①, 2 = ②, 3 = ③
  const [manualPickKey, setManualPickKey] = useState(null);
  const [manualTarget, setManualTarget] = useState(null);

  const goalsSessionRef = useRef(null);
  const abortRef = useRef(null);
  const scopeBootRef = useRef(false);
  const aiProposeGenRef = useRef(0);

  const proposalByLabel =
    proposalSource === 'ai'
      ? 'AI目標相談（実績・予測・伸びしろ）'
      : proposalSource === 'manual'
        ? '手動編集'
        : '目標設定';

  /**
   * Slots a scope works on: ?edit=kpiN → [N]. The whole-注力 menu / funnel → the slots that are
   * set now (TASK Q2), or [1] when none is — ②③ are added one at a time from「追加」.
   */
  // Slot being added from「注力指標をもう1つ追加しますか？」— kept on screen even before it holds a
  // KPI, so a failed AI proposal still leaves its card and「自分で選ぶ」(no dead end)
  const [addingSlot, setAddingSlot] = useState(null);
  const slotsFor = (s) => {
    if (isSlotScope(s)) return [SLOT_OF_SCOPE[s]];
    const set = addingSlot && !selectedSlots.includes(addingSlot) ? [...selectedSlots, addingSlot].sort() : selectedSlots;
    return set.length ? set : [1];
  };
  const activeSlots = slotsFor(scope);

  const buildConsultSummary = () => {
    const set = FOCUS_SLOTS.filter((s) => metricOf(s.slot)?.isSelected);
    const subDef = subKgiKey ? getFocusDef(subKgiKey) : null;
    return buildGoalsConsultSummary({
      kpis,
      selectedMonth,
      kpiHistory,
      mlForecasts,
      identityLine: KPI_IDENTITY_CHEATSHEET,
      gapLine: formatGapCandidatesLine(baseKpis, kpiHistory, baseMonth, 5, { subKgiKey }),
      draft: {
        salesGoal: sales,
        planMonths: months,
        subKgiKey,
        subKgiLabel: subDef ? `${subDef.label} 目標${fmtMetric(subDef.unit, subKgiTarget)}` : null,
        focusKeys: set.map((s) => metricOf(s.slot).key).join(',') || null,
        focusLabel: set.map((s) => `${s.label}${metricOf(s.slot).label}`).join(' / ') || null,
        focusCurrent: set.map((s) => metricOf(s.slot).value).join(' / ') || null,
        focusTarget: set.some((s) => targets[s.slot - 1] != null)
          ? set.map((s) => fmtMetric(metricOf(s.slot).unit, targets[s.slot - 1])).join(' / ')
          : null,
        proposalWhy: whys.filter(Boolean).join(' / '),
      },
    });
  };

  /**
   * Smallest legal target above (or below, for down-KPIs) a floor the proposal must clear.
   * Used only as the last resort: we raise the model's own number to the minimum that makes
   * sense rather than inventing one, so the screen always ends up with a usable proposal.
   */
  const clampToFloor = (key, target, floor) => {
    if (floor == null || target == null) return target;
    const def = getFocusDef(key);
    const down = def?.better === 'down';
    const t = Number(target);
    const f = Number(floor);
    if (!Number.isFinite(t) || !Number.isFinite(f)) return target;
    if (down ? t < f : t > f) return target;
    // Rates stay inside 0–100: a floor of 99.5% must not become 100.5%.
    if (def?.unit === 'percent') {
      return down
        ? Math.max(0, Math.round((f - 1) * 10) / 10)
        : Math.min(100, Math.round((f + 1) * 10) / 10);
    }
    if (def?.unit === 'currency') return down ? Math.floor((f - 100) / 100) * 100 : Math.ceil((f + 100) / 100) * 100;
    return down ? f - 1 : f + 1;
  };

  /** Floor a slot must clear: **this month's 実績 only**. 最高 is not a floor. */
  const floorFor = (key) => {
    if (!key) return null;
    return readFocusActual(key, baseKpis, kpiHistory, baseMonth);
  };

  /**
   * Apply AI GOAL_PROPOSAL to cards — reject if the target is not better than 実績.
   * With `clamp`, nothing is rejected: an under-target is raised to the smallest legal value
   * instead. The AI must always come back with something usable, so the last attempt clamps.
   */
  /**
   * @param slots    slot numbers this proposal may fill, e.g. [3] or [1, 2]
   * @param forbid   for「別の案で再計算」: { [slot]: key the slot must move away from }
   * @param allowKgi only the whole-注力 flows may take finalSales / planLength from the model;
   *   a single-slot edit must not rewrite KGI / period even if the model includes them
   */
  const applyAiProposalToCards = (pending, slots, clamp = false, forbid = null, allowKgi = false) => {
    const mapped = mapProposalToCardFields(pending);
    if (!mapped) throw new Error('GOAL_PROPOSAL を解釈できませんでした');
    const keyOf = (n) => mapped[FOCUS_SLOTS[n - 1].keyField] || metricOf(n).key;
    const targetField = (n) => FOCUS_SLOTS[n - 1].targetField;
    if (clamp) {
      slots.forEach((n) => {
        if (mapped[targetField(n)] != null) {
          mapped[targetField(n)] = clampToFloor(keyOf(n), mapped[targetField(n)], floorFor(keyOf(n)));
        }
      });
    }

    // 「別の案で再計算」asked for a different lever, so the same key back is a non-answer. Throwing
    // routes it into the caller's retry, which spells out the reason (that attempt clamps and
    // accepts, so the screen still ends with something usable).
    if (forbid) {
      const same = slots.find((n) => forbid[n] && keyOf(n) === forbid[n]);
      if (same) throw new Error(`注力${slotLabel(same)}が前回と同じ指標です。別のKPIに変えてください`);
    }

    // One KPI cannot hold two slots. The backend refuses such a save, but the card path used to
    // keep the number: the card de-dupes to 未選択 at render while 改善目標 and 根拠 stayed on
    // screen, leaving a target nobody could accept. Reject it here instead, so the caller's retry
    // asks for a different lever. The other slots' keys are the drafts, not the saved values.
    const proposed = slots.filter((n) => mapped[FOCUS_SLOTS[n - 1].keyField]);
    for (const n of proposed) {
      const key = mapped[FOCUS_SLOTS[n - 1].keyField];
      const clash = FOCUS_SLOTS.find(
        (o) => o.slot !== n && (slots.includes(o.slot) ? keyOf(o.slot) : metricOf(o.slot).key) === key
      );
      if (clash) {
        throw new Error(`注力${slotLabel(n)}が注力${clash.label}と同じ指標です。別のKPIに変えてください`);
      }
    }

    const proposal = { kind: 'goals', finalSales: allowKgi ? mapped.finalSales : undefined };
    const actuals = {
      salesActual: allowKgi && baseKpis?.total_sales != null ? Number(baseKpis.total_sales) : undefined,
    };
    const ACTUAL_FIELD = { 1: 'focusActual', 2: 'subFocusActual', 3: 'thirdFocusActual' };
    slots.forEach((n) => {
      const s = FOCUS_SLOTS[n - 1];
      const key = keyOf(n);
      proposal[s.keyField] = key || undefined;
      proposal[s.targetField] = mapped[s.targetField];
      actuals[ACTUAL_FIELD[n]] = key ? readFocusActual(key, baseKpis, kpiHistory, baseMonth) : undefined;
    });
    // kgiKeys: a KGI metric (売上 / 追加KGI) proposed as 注力 is refused like any bad target
    const check = validateGoalProposalTargets(proposal, actuals, { kgiKeys });
    if (!check.ok) throw new Error(check.reason || '提案値が現状実績を改善していません');

    const whyText = mapped.why || 'AIが実績・予測・伸びしろから算出した改善目標です。';
    slots.forEach((n) => {
      const s = FOCUS_SLOTS[n - 1];
      if (!(metricOf(n).isSelected || mapped[s.keyField]) || mapped[s.targetField] == null) return;
      if (mapped[s.keyField]) setDraftKey(n, mapped[s.keyField]);
      setTarget(n, mapped[s.targetField]);
      setWhy(n, whyText);
    });
    if (allowKgi && mapped.finalSales != null) setSales(mapped.finalSales);
    if (allowKgi && mapped.planLength != null) setMonths(mapped.planLength);
    setProposalSource('ai');
  };

  /**
   * Card propose via same AI chat 【目標相談】— never falls back to hybrid formula.
   */
  /**
   * @param slots slot numbers to propose, e.g. [1] / [1, 2] (see slotsFor)
   * @param opts.multi whole-注力 flow (menu / funnel) rather than one ?edit=kpiN slot. Passed in,
   *   not read from `scope`: the funnel calls this in the same tick as setScope(next).
   */
  const runAiProposeSlots = async (slots = [1], stanceV = 'standard', opts = {}) => {
    const gen = ++aiProposeGenRef.current;
    const setWhyFor = (text) => slots.forEach((n) => setWhy(n, text));

    // Unselected slots: AI picks key from GAP (do not force manual picker first)
    slots.forEach((n) => {
      if (metricOf(n).isSelected) return;
      setTarget(n, null);
      setWhy(n, `AIが注力${slotLabel(n)}と改善目標を選定しています…`);
    });
    if (!selectedStore || !selectedStylist) {
      setWhyFor('店舗とスタイリストを選んでからAI提案を取得してください。');
      setPhase('metric_proposed');
      return;
    }

    // No 実績 this month in this store → every proposal would be rejected (targets are checked
    // against this month's 実績), after up to 4 model calls. Say so plainly instead, and point at
    // the likely cause: the stylist was opened under a store they no longer work in.
    if (!loading && kpis?.total_sales == null) {
      const lastYm = (Array.isArray(kpiHistory) ? kpiHistory : [])
        .filter((r) => r?.total_sales != null && (r.month || r.ym))
        .map((r) => r.month || r.ym)
        .sort()
        .pop();
      const [, m] = String(selectedMonth || '').split('-');
      const monthLabel = m ? `${Number(m)}月` : '今月';
      const lastLabel = lastYm ? `${lastYm.slice(0, 4)}年${Number(lastYm.slice(5, 7))}月` : null;
      const msg =
        `${selectedStore} に${monthLabel}の実績がないため、AIは目標を提案できません。` +
        (lastLabel ? `この店舗での最新の実績は${lastLabel}です。` : '') +
        '店舗が正しいか確認するか、下の「自分で選ぶ」から設定してください。';
      setWhyFor(msg);
      setStanceNote(msg);
      setChatLog((p) => [...p, { role: 'ai', text: msg }]);
      setPhase('metric_proposed');
      return;
    }

    setPhase('analyzing');
    setAiTyping(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const summary = buildConsultSummary();
      let sid = goalsSessionRef.current;
      if (!sid) {
        const created = await createChatSession({
          store: selectedStore,
          stylist: selectedStylist,
          month: selectedMonth,
          kpi_summary: summary,
          signal: controller.signal,
        });
        sid = created.session_id;
        goalsSessionRef.current = sid;
      }

      // One ?edit=kpiN slot → 'kpiN'; the whole-注力 flows → 'kpi'
      const scopeKey = opts.multi ? 'kpi' : `kpi${slots[0]}`;
      const targetLine = scopeTargetLine(scopeKey, metrics, subKgiKey, slots);
      const autoText = buildAutoProposePrompt({
        scope: scopeKey,
        metrics,
        targets: FOCUS_SLOTS.map((s) =>
          metricOf(s.slot)?.isSelected && targets[s.slot - 1] != null
            ? fmtMetric(metricOf(s.slot).unit, targets[s.slot - 1])
            : null
        ),
        slots,
        subKgi: subKgiKey ? { key: subKgiKey, label: getFocusDef(subKgiKey)?.label || subKgiKey } : null,
        stance: stanceV,
        swapKey: !!opts.swapKey,
      });
      // 再計算 reuses the chat session, so without this the model sees its own previous answer
      // and repeats it — the stance hint alone is too weak to move the number.
      const avoid = [];
      if (opts.avoid) {
        slots.forEach((n) => {
          const m = metricOf(n);
          if (targets[n - 1] != null && m?.isSelected) {
            avoid.push(`注力${slotLabel(n)}「${m.label}」= ${fmtMetric(m.unit, targets[n - 1])}`);
          }
        });
      }
      const prevLine = avoid.length ? `前回の提案は ${avoid.join(' / ')} でした。` : '';
      const avoidLine = opts.swapKey
        ? `${prevLine}今回は**指標そのもの**を変えてください（同じkeyは却下されます）。` +
          '数値だけの変更ではなく、GAP候補から別のKPIを選び直してください。理由も前回と変えてください。'
        : avoid.length
          ? `${prevLine}今回は${STANCE_LABEL[stanceV] || '別案'}として、` +
            '**必ず別の数値**を出してください（同じ値は却下されます）。指標（key）は変えないでください。理由も前回と変えてください。'
          : null;

      const outbound = [
        '【目標相談】',
        targetLine || null,
        avoidLine,
        autoText,
      ]
        .filter(Boolean)
        .join('\n');

      // The first proposal is shown as the card alone — no fake "改善目標をAIに提案してもらう"
      // user turn and no prose bubble. The card already carries 現状 / 改善目標 / 根拠, so the
      // model's paragraph only repeated it. The call itself still runs: GOAL_PROPOSAL is what
      // paints the card.
      const ask = async (extra) =>
        postChatMessageStream(sid, extra ? `${outbound}\n${extra}` : outbound, {
          signal: controller.signal,
          kpi_summary: summary,
        });

      let res = await ask(null);
      if (gen !== aiProposeGenRef.current) return;

      // A target under a level already reached is rejected — but rejecting must not dead-end
      // the screen. Ask once more with the floor spelled out, and only give up after that.
      try {
        applyAiProposalToCards(
          res.pending_proposal || res.assistant_message?.pending_proposal,
          slots,
          false,
          opts.swapKey
            ? Object.fromEntries(slots.map((n) => [n, metricOf(n)?.key || null]))
            : null,
          !!opts.multi
        );
      } catch (firstErr) {
        res = await ask(
          `前回の提案は却下されました: ${firstErr?.message || '提案値が実績を改善していません'}。` +
            'この条件を満たす値で、GOAL_PROPOSAL を出し直してください。'
        );
        if (gen !== aiProposeGenRef.current) return;
        // Last attempt clamps instead of throwing — the user always gets a usable proposal.
        // (A KGI key or a duplicate cannot be clamped away: those still end in「提案を出せません」.)
        applyAiProposalToCards(
          res.pending_proposal || res.assistant_message?.pending_proposal,
          slots,
          true,
          null,
          !!opts.multi
        );
      }
      setStanceNote(
        `${STANCE_LABEL[stanceV] || '標準案'}（AI）で提案しました。合わなければ「自分で選ぶ」か再計算してください。`
      );
    } catch (err) {
      if (gen !== aiProposeGenRef.current) return;
      const msg = err?.message || '提案を出せませんでした。';
      // Keep prior targets (saved/draft). Never invent a hybrid number on failure.
      setWhyFor(`提案を出せません: ${msg}`);
      // The real reason (e.g. 「注力①の現状実績が無いため…」), not a generic failure.
      setStanceNote(`AIが提案を出せませんでした: ${msg} 下の「自分で選ぶ」から指標を選べます。`);
      setChatLog((p) => {
        const next = p.filter((m) => !(m.role === 'ai' && m.streaming && !m.text));
        return [...next, { role: 'ai', text: `提案を出せません: ${msg}` }];
      });
    } finally {
      if (gen === aiProposeGenRef.current) {
        setAiTyping(false);
        setPhase('metric_proposed');
      }
    }
  };

  /** 数値だけ引き直す — same KPI, next stance, new number (no hybrid formula). */
  const recalcSameKpi = () => {
    const order = ['standard', 'aggressive', 'conservative'];
    const next = order[(order.indexOf(stance) + 1) % order.length];
    setStance(next);
    void runAiProposeSlots(activeSlots, next, { avoid: true, multi: !isSlotScope(scope) });
  };

  /** 別の案 — the lever itself changes. Stance stays: it is a different KPI, not a bolder number. */
  const recalcOtherKpi = () => {
    void runAiProposeSlots(activeSlots, stance, { avoid: true, swapKey: true, multi: !isSlotScope(scope) });
  };

  /**
   * 「この注力を外す」(②③ only — ① is required). Stops a running AI proposal and goes straight to
   * the confirm step; save() then writes the slot as explicit nulls (TASK 5章の1).
   */
  const removeFocusSlot = (n) => {
    if (n < 2) return;
    aiProposeGenRef.current += 1;
    abortRef.current?.abort();
    setAiTyping(false);
    setManualMetricEditing(false);
    setRemoveSlot(n);
    logUser(`注力指標${slotLabel(n)}（${metricOf(n)?.label || '未選択'}）を外す`);
    setPhase('scoped_ready');
  };

  // Deep-link boot: AI propose KPI / seed actions once metrics are ready
  useEffect(() => {
    if (scopeBootRef.current) return;
    if (!initialScope) {
      scopeBootRef.current = true;
      return;
    }
    if (isGoalSetupScope(initialScope)) {
      scopeBootRef.current = true;
      return;
    }
    if (!metrics) return;
    scopeBootRef.current = true;
    if (isSlotScope(initialScope) || initialScope === 'kpi') {
      // Always AI-first (picks key if 未選択). Manual picker only if user rejects the AI card.
      setPhase('analyzing');
      void runAiProposeSlots(slotsFor(initialScope), 'standard', { multi: initialScope === 'kpi' });
      return undefined;
    }
    if (initialScope === 'actions') {
      setActions(goals?.actions?.length ? goals.actions : []);
      setPhase('actions_proposed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once from URL
  }, [initialScope, metrics]);

  const sendSoudan = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || aiTyping) return;
    if (!selectedStore || !selectedStylist) {
      setChatLog((p) => [
        ...p,
        { role: 'user', text: trimmed },
        { role: 'ai', text: 'ホームで店舗とスタイリストを選んでから相談してください。' },
      ]);
      return;
    }

    setChatLog((p) => [...p, { role: 'user', text: trimmed }]);
    setAiTyping(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      if (isGoalsConfirmChip(trimmed)) {
        let sid = goalsSessionRef.current;
        if (!sid) throw new Error('セッションがありません。先に相談を送ってください');
        const res = await confirmProposedActions({ sessionId: sid, kind: 'goals' });
        if (res?.goals) {
          notifyGoalsUpdated(res.goals);
          setGoals(res.goals);
          if (res.goals.finalSales != null) setSales(res.goals.finalSales);
          if (res.goals.planLength != null) setMonths(res.goals.planLength);
          // 注力 keys / targets follow the saved goals via the sync effect (null included);
          // clear ephemeral why until next propose
          clearWhys();
          setStanceNote('');
          setManualMetricEditing(false);
          if (scoped) setPhase('scoped_ready');
        }
        setChatLog((p) => [
          ...p,
          {
            role: 'ai',
            text: `目標設定を保存しました。注力指標①は${
              getFocusDef(res?.goals?.focusKey)?.label || res?.goals?.focusKey || '更新後のKPI'
            }です。設定画面やホームに反映されます。`,
          },
        ]);
        setToastText('目標設定を保存しました');
        setToast(true);
        setTimeout(() => setToast(false), 1400);
        return;
      }

      const summary = buildConsultSummary();
      let sid = goalsSessionRef.current;
      if (!sid) {
        const created = await createChatSession({
          store: selectedStore,
          stylist: selectedStylist,
          month: selectedMonth,
          kpi_summary: summary,
          signal: controller.signal,
        });
        sid = created.session_id;
        goalsSessionRef.current = sid;
      }

      setChatLog((p) => [...p, { role: 'ai', text: '', streaming: true }]);
      const targetLine = scopeTargetLine(scope, metrics, subKgiKey, activeSlots);
      const outbound = targetLine
        ? `【目標相談】\n${targetLine}\n${trimmed}`
        : `【目標相談】${trimmed}`;
      const res = await postChatMessageStream(sid, outbound, {
        signal: controller.signal,
        kpi_summary: summary,
        onDelta: (chunk) => {
          setChatLog((p) => {
            const next = [...p];
            const last = next[next.length - 1];
            if (last?.role === 'ai' && last.streaming) {
              next[next.length - 1] = { role: 'ai', text: (last.text || '') + chunk, streaming: true };
            }
            return next;
          });
        },
      });
      const reply = res.assistant_message?.content || '応答を取得できませんでした。';
      const choices = res.assistant_message?.choices || [];
      setChatLog((p) => {
        const next = [...p];
        const last = next[next.length - 1];
        if (last?.role === 'ai' && last.streaming) {
          next[next.length - 1] = { role: 'ai', text: reply, choices };
        } else {
          next.push({ role: 'ai', text: reply, choices });
        }
        return next;
      });

      // Free-form may also return GOAL_PROPOSAL — paint card when valid (never ≤ 実績)
      const pending = res.pending_proposal || res.assistant_message?.pending_proposal;
      if (pending?.kind === 'goals' && (editTarget === 'metric' || isSlotScope(scope) || scope === 'kpi')) {
        try {
          applyAiProposalToCards(pending, activeSlots, false, null, !isSlotScope(scope));
          if (phase === 'analyzing' || phase === 'metric_proposed') setPhase('metric_proposed');
        } catch (ve) {
          setChatLog((p) => [
            ...p,
            { role: 'ai', text: `提案値をカードに反映できませんでした: ${ve.message}` },
          ]);
        }
      }
    } catch (err) {
      const msg = err?.message || '相談に失敗しました。もう一度送ってください。';
      setChatLog((p) => {
        const next = p.filter((m) => !(m.role === 'ai' && m.streaming && !m.text));
        return [...next, { role: 'ai', text: msg }];
      });
    } finally {
      setAiTyping(false);
    }
  };

  const CHOICE_LABEL = {
    goal: '目標売上・期間を変更したいです',
    metric: '注力指標（KPI）を変更したいです',
    actions: '行動プランを変更したいです',
  };

  const chooseGoal = () => {
    setScope(null);
    setEditTarget('goal');
    logUser(CHOICE_LABEL.goal);
    setPhase('goal_setup');
  };

  const startMetricFlow = (nextScope = null) => {
    setEditTarget('metric');
    setStance('standard');
    setStanceNote('');
    setProposalSource(null);
    setManualMetricEditing(false);
    setRemoveSlot(null);
    setAddingSlot(null);
    setDecided({ sales, months, deadlineLabel });
    setPhase('analyzing');
    void runAiProposeSlots(slotsFor(nextScope), 'standard', { multi: !isSlotScope(nextScope) });
  };

  const chooseMetric = () => {
    setScope(null);
    logUser(CHOICE_LABEL.metric);
    startMetricFlow(null);
  };

  const chooseActions = () => {
    setScope(null);
    setEditTarget('actions');
    logUser(CHOICE_LABEL.actions);
    setDecided({ sales, months, deadlineLabel });
    // Actions-only: keep existing focus targets; do not invent hybrid numbers
    setActions(goals?.actions?.length ? goals.actions : []);
    setPhase('actions_proposed');
    if (!goals?.actions?.length) void proposeAltActions();
  };

  /** The 注力 drafts as a goals-shaped object (for dropFocusKey). */
  const draftFocusGoals = () =>
    Object.fromEntries(
      FOCUS_SLOTS.flatMap((s, i) => [
        [s.keyField, draftKeys[i]],
        [s.targetField, targets[i]],
      ])
    );

  /**
   * KGI wins (TASK 5章の3): a 注力 holding the new 追加KGI is dropped and the remaining slots move
   * up — no confirm dialog, just a note. Applied to the drafts here; save() sends the same drop in
   * the one PATCH that carries the KGI.
   */
  const applyKgiDropToDrafts = () => {
    const { patch, dropped } = dropFocusKey(draftFocusGoals(), subKgiKey);
    if (!dropped.length) {
      setDroppedNote('');
      return;
    }
    setDraftKeys(FOCUS_SLOTS.map((s) => patch[s.keyField]));
    setTargets(FOCUS_SLOTS.map((s) => patch[s.targetField]));
    const label = getFocusDef(subKgiKey)?.label || subKgiKey;
    const note =
      `${label}をKGIにしたので、注力${dropped.map((s) => s.label).join('')}から外しました。` +
      (patch.focusKey ? '' : '保存したあと、注力指標①を選び直してください。');
    setDroppedNote(note);
    logAi(note);
  };

  /** Goal / period only — never enter KPI propose. */
  const decideGoal = () => {
    setDecided({ sales, months, deadlineLabel });
    const subDef = subKgiKey ? getFocusDef(subKgiKey) : null;
    logUser(
      scope === 'period'
        ? `目標期間を ${periodLabel} にする`
        : scope === 'subkgi'
          ? `追加KGIを ${subDef ? `${subDef.label} ${fmtMetric(subDef.unit, subKgiTarget)}` : 'なし'} にする`
          : `目標売上 ${yen(sales)}` +
            (subDef ? `・${subDef.label} ${fmtMetric(subDef.unit, subKgiTarget)}` : '') +
            `・目標期間 ${periodLabel} で進める`
    );
    if (scope !== 'period') applyKgiDropToDrafts();
    if (isGoalSetupScope(scope)) {
      setEditTarget('goal');
      clearWhys();
      if (funnelStep) {
        void funnelSaveAndNext(scope);
        return;
      }
      setPhase('scoped_ready');
      return;
    }
    // Full funnel from choose: KGI set → then AI KPI propose — on the next render, so the proposal
    // sees the drafts after the KGI drop above (in this tick they still hold the dropped key).
    setMetricStartPending(true);
  };

  const [metricStartPending, setMetricStartPending] = useState(false);
  useEffect(() => {
    if (!metricStartPending) return;
    setMetricStartPending(false);
    startMetricFlow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per request
  }, [metricStartPending]);

  // Enter the first-run sequence once, and only once the saved goal is actually known.
  // Runs at most one transition: after this the user is driving, and a late goals refresh
  // must not yank the screen back to the start.
  const funnelDecidedRef = useRef(false);
  useEffect(() => {
    if (funnelDecidedRef.current || goalsLoading) return;
    funnelDecidedRef.current = true;
    // 初期設定 left unfinished (setupStep = the next step) resumes there. Accounts set up before
    // setupStep existed have no such field and keep the item menu.
    const resume = resumeStepFrom(goals);
    if (!resume && hasSavedGoal) return;
    // ?edit=kpi1 / kpi2 / actions is a deliberately narrow intent — leave it alone.
    if (initialScope && !isGoalSetupScope(initialScope)) return;
    if (resume === 'kpi1') {
      // AI proposal needs the POS snapshot — wait for a tap instead of firing on load
      const v = funnelStepView('kpi1', { resume: true });
      setFunnelStep('kpi1');
      setScope(v.scope);
      setEditTarget(v.editTarget);
      // the step blocks render under `decided` — seed it from the saved KGI
      setDecided({ sales: goals?.finalSales ?? sales, months: goals?.planLength ?? months, deadlineLabel });
      setPhase(v.phase);
      return;
    }
    if (resume) {
      enterFunnelStep(resume);
      return;
    }
    enterFunnelStep('goal');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- decides once when goals are known
  }, [goalsLoading, hasSavedGoal, initialScope]);

  // One plain thread: the step line is said once, in place, when the step starts — it is a chat
  // message like any other, not a header pinned under the log. The unscoped "which item?" line is
  // not archived: it belongs to the choose buttons below it, not to the conversation.
  const introScopeRef = useRef(null);
  useEffect(() => {
    if (introScopeRef.current === scope) return;
    introScopeRef.current = scope;
    if (!isScopedSlice(scope)) return;
    const html = scopeIntroHtml(scope);
    if (html) setChatLog((prev) => [...prev, { role: 'ai', html }]);
  }, [scope]);

  /**
   * The step cards are live UI: they are replaced as the funnel moves, so what the user chose
   * and what the manager proposed used to vanish, leaving a thread of nothing but step lines.
   * Every decision is written into the thread at the moment it is made.
   */
  const logUser = (text) => {
    const t = (text || '').trim();
    if (t) setChatLog((prev) => [...prev, { role: 'user', text: t }]);
  };
  const logAi = (text) => {
    const t = (text || '').trim();
    if (t) setChatLog((prev) => [...prev, { role: 'ai', text: t }]);
  };

  /**
   * Enter one step of the first-run sequence (order and per-step state: utils/setupFunnel.js).
   * One slot at a time on purpose: ② is chosen knowing what ① turned out to be, and the action
   * plan is built on top of them. ②③ are optional, so those steps ask first (phase 'ask_slot')
   * instead of calling the model straight away.
   */
  const enterFunnelStep = (step) => {
    const v = funnelStepView(step);
    if (!v) return;
    setFunnelStep(step);
    if (step) setStanceNote('');
    setScope(v.scope);
    setEditTarget(v.editTarget);
    if (step === 'actions' || step === 'kpi2' || step === 'kpi3') setDecided({ sales, months, deadlineLabel });
    if (v.phase) setPhase(v.phase);
    // Ask the model for the plan straight away, built on the 注力 just agreed.
    if (v.propose === 'actions' && !goals?.actions?.length) void proposeAltActions();
    if (v.propose === 'metric') startMetricFlow(step);
  };
  const goToNextFunnelStep = () => enterFunnelStep(nextFunnelStep(funnelStep));

  /**
   * 初期設定: one tap saves this step and opens the next one. `setupStep` (= the NEXT step) is
   * written with the same save, so the sequence resumes there if the screen is closed. A failed
   * save stays on the step (save() puts the reason in the thread).
   */
  const funnelSaveAndNext = async (scopeToSave) => {
    const ok = await save(scopeToSave, { setupStep: setupStepAfter(funnelStep) });
    if (ok) enterFunnelStep(nextFunnelStep(funnelStep));
  };
  /** A step skipped without a save (「設定しない」) still moves `setupStep` on. */
  const writeSetupStep = (step) => {
    if (!canEditGoals) return;
    void setGoals({ ...(goals || {}), setupStep: step }, { patchFields: { setupStep: step } });
  };

  /** 'ask_slot' → 設定する: propose this optional slot. */
  const acceptOptionalSlot = () => {
    logUser(`注力指標${slotLabel(SLOT_OF_SCOPE[scope])}も設定する`);
    startMetricFlow(scope);
  };
  /** 'ask_slot' → 設定しない: an empty ② makes ③ moot, so both skip straight to the plan. */
  const skipOptionalSlots = () => {
    logUser(`注力指標${slotLabel(SLOT_OF_SCOPE[scope])}は設定しない`);
    writeSetupStep(SKIP_OPTIONAL_TARGET);
    enterFunnelStep(SKIP_OPTIONAL_TARGET);
  };

  const resetGoal = () => {
    setFunnelStep(null);
    setScope(null);
    setEditTarget(null);
    setDecided(null);
    clearWhys();
    setStance('standard');
    setStanceNote('');
    setManualEditing(false);
    setManualMetricEditing(false);
    setRemoveSlot(null);
    setDroppedNote('');
    setAddingSlot(null);
    setDraftKeys(savedKeys);
    setTargets(savedTargets);
    setSubKgiKey(savedSubKgi?.key ?? null);
    setSubKgiTarget(savedSubKgi?.target ?? null);
    setPhase('choose');
  };

  /** Keys the other slots hold (drafts) — a slot may not take one of them. */
  const otherSlotKeys = (slot) =>
    FOCUS_SLOTS.filter((s) => s.slot !== slot)
      .map((s) => metricOf(s.slot)?.key)
      .filter(Boolean);

  /**
   * Seed the stepper from the snapshot's own number. Starting at 0 meant thousands of taps to
   * reach a real target (¥100 step -> ¥1,000,000). Still the user's number: they step from 現状.
   */
  const seedManualTarget = (key) =>
    key ? readFocusActual(key, baseKpis, kpiHistory, baseMonth) : null;

  const startManualMetricEdit = (slot) => {
    const s = FOCUS_SLOTS.some((f) => f.slot === slot) ? slot : 1;
    const curKey = metricOf(s).key;
    setManualMetricSlot(s);
    setManualPickKey(curKey);
    setManualTarget(targets[s - 1] ?? seedManualTarget(curKey));
    setManualMetricEditing(true);
  };

  /** A key the manual picker may take for `slot`: a lever (not a KGI, draft 追加KGI included)
   *  that no other slot holds. */
  const canPickKey = (key, slot) =>
    focusLeverKeysFor({ subKgiKey }).includes(key) && !otherSlotKeys(slot).includes(key);

  const onManualPickKey = (key) => {
    if (!canPickKey(key, manualMetricSlot)) return;
    setManualPickKey(key);
    // No hybrid formula — the stepper just opens on the KPI's current snapshot value.
    setManualTarget(seedManualTarget(key));
  };

  const applyManualMetricEdit = () => {
    if (!manualPickKey || !canPickKey(manualPickKey, manualMetricSlot)) return;
    if (manualTarget == null || Number.isNaN(Number(manualTarget))) {
      setStanceNote('改善目標の数値を入力してから適用してください（自動計算はしません）。');
      return;
    }
    const label = slotLabel(manualMetricSlot);
    const card = buildFocusMetricCard(manualPickKey, label, baseKpis, kpiHistory, baseMonth);
    const tgt = Number(manualTarget);
    setDraftKey(manualMetricSlot, manualPickKey);
    setTarget(manualMetricSlot, tgt);
    setWhy(manualMetricSlot, `手動選択: ${card.label} → 改善目標 ${fmtMetric(card.unit, tgt)}`);
    setProposalSource('manual');
    setStanceNote(`${card.label}に変更しました（手動）。AI再提案は「別の案で再計算」から。`);
    logUser(`注力指標${label} を手動で選択: ${card.label} → ${fmtMetric(card.unit, tgt)}`);
    setManualMetricEditing(false);
  };

  // No fallback KPI: until the user taps one, the picker has no selection
  const manualPickDef = manualPickKey ? FOCUS_KPI_DEFS[manualPickKey] ?? null : null;
  const manualPickActual = manualPickKey
    ? readFocusActual(manualPickKey, baseKpis, kpiHistory, baseMonth)
    : null;
  const manualStep =
    manualPickDef?.unit === 'percent' ? 1 : manualPickDef?.unit === 'currency' ? 100 : 1;
  const manualMin = 0;
  const manualMax =
    manualPickDef?.unit === 'percent' ? 100 : manualPickDef?.unit === 'currency' ? 10000000 : 10000;

  // Every slot on screen needs a KPI and a 改善目標 — and only those slots: ②③ are optional,
  // so an unset ② no longer blocks a ①-only setup.
  const metricAcceptBlocked = (() => {
    if (aiTyping || phase === 'analyzing') return true;
    return activeSlots.some((n) => !metricOf(n).isSelected || targets[n - 1] == null);
  })();

  const acceptMetric = () => {
    if (metricAcceptBlocked) {
      setStanceNote('有効な改善目標がありません。AI提案を待つ・再計算する・手動で数値を入れてください。');
      return;
    }
    // Typed / chosen number saves as-is. No 最高 floor, no 現状 floor on the user.
    // Card → thread, before the card is replaced by the next step.
    const lines = activeSlots
      .filter((n) => targets[n - 1] != null && metricOf(n)?.label)
      .map((n) => {
        const m = metricOf(n);
        return `注力指標${slotLabel(n)} ${m.label}：現状 ${m.value} → 改善目標 ${fmtMetric(m.unit, targets[n - 1])}`;
      });
    logAi(lines.join('\n'));
    logUser(lines.length > 1 ? `この${lines.length}指標で進める` : 'この案で進める');
    // まとめて見直す (Q2): after the set slots, offer one more slot while there is room
    if ((!scope || scope === 'kpi') && selectedSlots.length < 3) {
      setPhase('ask_more');
      return;
    }
    if (funnelStep && isSlotScope(scope)) {
      void funnelSaveAndNext(scope);
      return;
    }
    finishMetricStep();
  };

  const finishMetricStep = () => {
    if (isSlotScope(scope) || scope === 'kpi') {
      setPhase('scoped_ready');
      return;
    }
    setPhase('metric_done');
    setTimeout(() => setPhase('actions_proposed'), 700);
  };

  /** 'ask_more' → 追加する: propose the first empty slot; the cards then show it with the others. */
  const addMoreSlot = () => {
    const n = [1, 2, 3].find((s) => !selectedSlots.includes(s));
    if (!n) return finishMetricStep();
    logUser(`注力指標${slotLabel(n)}を追加する`);
    setRemoveSlot(null);
    setAddingSlot(n);
    void runAiProposeSlots([n], stance);
    return undefined;
  };

  /** Drop a slot that was being added but never got a KPI (AI failed / user changed their mind). */
  const cancelAddSlot = () => {
    if (!addingSlot) return;
    setDraftKey(addingSlot, null);
    setTarget(addingSlot, null);
    setWhy(addingSlot, '');
    logUser(`注力指標${slotLabel(addingSlot)}の追加をやめる`);
    setAddingSlot(null);
    finishMetricStep();
  };

  const acceptActions = () => {
    const list = actions
      .map((a, i) => `${i + 1}. ${a.name}${a.cadence ? `（${a.cadence}）` : ''}`)
      .join('\n');
    logAi(list ? `毎日の行動プラン\n${list}` : '');
    logUser('この行動プランでやる');
    if (funnelStep && scope === 'actions') {
      void funnelSaveAndNext('actions');
      return;
    }
    if (scope === 'actions') {
      setPhase('scoped_ready');
      return;
    }
    setPhase('actions_done');
  };

  /**
   * Ask the model for a fresh plan tied to the 注力 actually saved, instead of swapping in a
   * fixed template. The canned set stays as the fallback so the screen always shows a plan.
   */
  const proposeAltActions = async () => {
    const fallback = () => {
      // One canned action per 指標 where we have one; the old mixed set only when none match
      const perKpi = fallbackActionsFor(actionKeys);
      setActions(
        perKpi.length
          ? perKpi
          : [
              ...(ALT_ACTIONS.newReturn || []).slice(0, 2),
              ...(ALT_ACTIONS.unitPrice || []).slice(0, 1),
            ].map((a, i) => ({ ...a, id: i + 1 }))
      );
    };
    if (!selectedStore || !selectedStylist) {
      fallback();
      return;
    }
    setAiTyping(true);
    try {
      const summary = buildConsultSummary();
      let sid = goalsSessionRef.current;
      if (!sid) {
        const created = await createChatSession({
          store: selectedStore,
          stylist: selectedStylist,
          month: selectedMonth,
          kpi_summary: summary,
        });
        sid = created.session_id;
        goalsSessionRef.current = sid;
      }
      const current = actions.map((a) => a.name).filter(Boolean);
      const res = await postChatMessageStream(sid, actionsRequestText(actionKeys, subKgiKey, current), {
        kpi_summary: summary,
      });
      const pending = res.pending_proposal || res.assistant_message?.pending_proposal;
      const list = Array.isArray(pending?.actions) ? pending.actions : [];
      const mapped = list
        .slice(0, MAX_PLAN_ACTIONS)
        .map((a, i) => ({
          id: i + 1,
          name: String(a?.name || '').trim(),
          cadence: String(a?.when || a?.howMuch || '毎日').trim(),
          // The server already validated the key; keep it so the plan is saved per 指標
          focusKey: a?.focusKey || null,
          when: a?.when,
          what: a?.what,
          howMuch: a?.howMuch,
        }))
        .filter((a) => a.name);
      // Show the model's own explanation, same as 改善策を相談 does. The cards only carry
      // name + cadence, so いつ・何を・どのくらい would be lost if we kept the ACTIONS line only.
      const reply = (res.assistant_message?.content || '').trim();
      if (reply) setChatLog((prev) => [...prev, { role: 'ai', text: reply }]);
      if (mapped.length) setActions(mapped);
      else fallback();
    } catch {
      fallback();
    } finally {
      setAiTyping(false);
    }
  };

  // --- Inline manual action editing (stays in this chat, no route change) ---
  const MANUAL_CADENCES = CADENCES; // shared with ActionEditScreen and the 管理画面 sheet

  const startManualEdit = () => {
    setManualActs(actions.map((a, i) => ({
      ...a,
      id: a.id ?? i + 1,
      name: a.name || '',
      cadence: a.cadence || MANUAL_CADENCES[2],
      focusKey: a.focusKey || null,
    })));
    setManualEditing(true);
  };

  const setManualActName = (i, v) =>
    setManualActs((p) => p.map((a, j) => (j === i ? { ...a, name: v } : a)));
  const setManualActCadence = (i, v) =>
    setManualActs((p) => p.map((a, j) => (j === i ? { ...a, cadence: v } : a)));
  const setManualActFocus = (i, k) =>
    setManualActs((p) => p.map((a, j) => (j === i ? { ...a, focusKey: k } : a)));
  const removeManualAct = (i) => setManualActs((p) => p.filter((_, j) => j !== i));
  // A new row starts on the only 指標 when there is just one (add more for that 指標)
  const addManualAct = () =>
    setManualActs((p) => [
      ...p,
      { id: Date.now(), name: '', cadence: MANUAL_CADENCES[2], focusKey: actionKeys.length === 1 ? actionKeys[0] : null },
    ]);

  const applyManualEdit = () => {
    const normalized = manualActs
      .filter((a) => a.name.trim())
      // Keep actionId / いつ・何を・どのくらい: dropping them made every manual edit a brand-new action
      .map((a, i) => ({ ...a, id: i + 1, name: a.name.trim(), cadence: a.cadence, focusKey: a.focusKey || null }));
    if (normalized.length === 0) return;
    setActions(normalized);
    logUser(
      `行動プランを手動で編集\n${normalized.map((a, i) => `${i + 1}. ${a.name}（${a.cadence}）`).join('\n')}`
    );
    setManualEditing(false);
  };

  const save = async (saveScope = scope, { setupStep = null } = {}) => {
    // Own record only (goals_service.can_write_stylist_goals). Refuse out loud:
    // falling through would run setGoals() as a no-op and still fire the
    // 保存しました toast — a save that lies is worse than a refusal.
    if (!canEditGoals) {
      setChatLog((prev) => [
        ...prev,
        {
          role: 'ai',
          text: `保存できません。編集できるのは自分の目標だけです。${
            selectedStylist ? `${selectedStylist}さんの目標は閲覧のみです。` : ''
          }`,
        },
      ]);
      return false;
    }
    // Derived, never typed: last day of (planStartMonth + planLength - 1)
    const goalDate = deadlineYmd;
    const prevPlanLength = goals?.planLength ?? null;
    // Changelog 目標期間 before/after read like the picker:「〜2027年3月（6ヶ月）」
    const prevPeriodLabel =
      prevPlanLength != null
        ? `〜${formatMonthLabel(resolvePlanPeriod(goals)?.endMonth) ?? EM}（${prevPlanLength}ヶ月）`
        : null;
    const slotN = SLOT_OF_SCOPE[saveScope] || null;
    const touchesKgi = !saveScope || saveScope === 'goal';
    // 追加KGI is its own slice (`subkgi`); the 売上 slice (`goal`) no longer touches it
    const touchesSubKgi = !saveScope || saveScope === 'subkgi';
    const touchesFocus = !saveScope || saveScope === 'kpi' || Boolean(slotN);

    // 注力①②③ are always written as a set, nulls included: a slot that is not sent keeps its old
    // key on the server (TASK 5章の1). Keys come from the cards — already de-duplicated and free of
    // the draft 追加KGI; targets travel with their keys.
    const focusPatch = {};
    if (touchesFocus) {
      const removing = slotN && removeSlot === slotN;
      const drop = removing ? dropFocusKey(draftFocusGoals(), draftKeys[slotN - 1]) : null;
      FOCUS_SLOTS.forEach((s, i) => {
        const key = drop?.dropped.length ? drop.patch[s.keyField] : metricOf(s.slot).key || null;
        focusPatch[s.keyField] = removing && !drop?.dropped.length && s.slot === slotN ? null : key;
        focusPatch[s.targetField] = drop?.dropped.length
          ? drop.patch[s.targetField]
          : focusPatch[s.keyField]
            ? targets[i] ?? null
            : null;
      });
    }

    // KGI = 売上 + 追加KGI. KGI wins: a stored 注力 equal to the new 追加KGI leaves 注力 in the SAME
    // PATCH (the BE refuses a save that creates the overlap), found in the RAW goals (TASK 5章の3・5).
    const subKgiPatch = touchesSubKgi
      ? { subKgiKey: subKgiKey || null, subKgiTarget: subKgiKey ? subKgiTarget ?? null : null }
      : {};
    const kgiPatch = touchesKgi
      ? { finalSales: sales, salesTarget: sales, planLength: months, planStartMonth, goalDate, ...subKgiPatch }
      : subKgiPatch;
    const kgiDrop = touchesSubKgi && saveScope ? dropFocusKey(goals, subKgiKey) : { patch: {}, dropped: [] };
    Object.assign(focusPatch, kgiDrop.patch);

    if (touchesFocus && slotN > 1 && removeSlot !== slotN && !focusPatch[FOCUS_SLOTS[slotN - 1].keyField]) {
      // A compaction (removing ②) can legitimately move ③ into ②; a plain ②③ save needs its key.
      setChatLog((prev) => [
        ...prev,
        { role: 'ai', text: `注力指標${slotLabel(slotN)}が未選択です。「自分で選ぶ」から選ぶか、この枠を外してください。` },
      ]);
      return false;
    }
    if ((saveScope === 'kpi' || saveScope === 'kpi1' || !saveScope) && !focusPatch.focusKey) {
      // ① is required — on screen. The BE does not check it: first run saves the KGI before ①.
      setChatLog((prev) => [
        ...prev,
        { role: 'ai', text: '注力指標①が未選択です。「①を自分で選ぶ」から選んでから保存してください。' },
      ]);
      return false;
    }

    const after = { ...(goals || {}), ...kgiPatch, ...focusPatch };
    // chartTargets follow the goal metrics: a KPI that stopped being a goal loses its chart 目標,
    // or re-picking it later shows a stale number next to the card's (TASK 5章の6).
    const goalKeysOf = (g) => [...FOCUS_SLOTS.map((s) => g?.[s.keyField]), g?.subKgiKey].filter(Boolean);
    const nextChartTargets = { ...(goals?.chartTargets || {}) };
    goalKeysOf(goals)
      .filter((k) => !goalKeysOf(after).includes(k))
      .forEach((k) => {
        const ck = chartKeyForFocus(k);
        if (ck && ck !== 'revenue') delete nextChartTargets[ck];
      });
    FOCUS_SLOTS.forEach((s) => {
      const ck = chartKeyForFocus(after[s.keyField]);
      // 売上 is the KGI's chart — a legacy 注力=総売上 must not overwrite it
      if (ck && ck !== 'revenue' && after[s.targetField] != null) nextChartTargets[ck] = after[s.targetField];
    });
    const subCk = chartKeyForFocus(after.subKgiKey);
    if (subCk && after.subKgiTarget != null) nextChartTargets[subCk] = after.subKgiTarget;
    if (touchesKgi) nextChartTargets.revenue = sales;

    // Changelog rows
    const fmtSlot = (g, s) => {
      const def = getFocusDef(g?.[s.keyField]);
      return def ? `${def.label} → ${fmtMetric(def.unit, g?.[s.targetField])}` : null;
    };
    const focusChanges = FOCUS_SLOTS.map((s) => ({
      field: `注力指標${s.label}`,
      before: fmtSlot(goals, s),
      after: fmtSlot(after, s) || 'なし',
    })).filter((c) => (c.before || 'なし') !== c.after);
    const fmtSubKgi = (g) => {
      const def = getFocusDef(g?.subKgiKey);
      return def ? `${def.label} ${fmtMetric(def.unit, g?.subKgiTarget)}` : 'なし';
    };
    const kgiChanges = [
      { field: '単月売上ゴール', before: gt.finalSales != null ? yen(gt.finalSales) : null, after: yen(sales) },
      ...(fmtSubKgi(goals) !== fmtSubKgi(after)
        ? [{ field: '追加KGI', before: fmtSubKgi(goals), after: fmtSubKgi(after) }]
        : []),
    ];
    const whyJoined = whys.filter(Boolean).join(' / ');

    const actionsForSubmit = actions
      .filter((a) => a?.name?.trim())
      .map((a) => ({
        actionId: a.actionId,
        name: a.name.trim(),
        cadence: a.cadence,
        when: a.when,
        what: a.what,
        howMuch: a.howMuch,
        focusKey: a.focusKey || null,
      }));
    // focusKey counts as a change: re-tagging an action must reach the server
    const planKey = (list) =>
      JSON.stringify((list || []).map((a) => [a.name, a.cadence ?? null, a.focusKey ?? null]));
    const actionsChanged = planKey(actionsForSubmit) !== planKey(goals?.actions);
    const sendActions = () =>
      submitActions(actionsForSubmit).catch((err) => {
        setChatLog((prev) => [
          ...prev,
          { role: 'ai', text: `行動プランを送信できませんでした。${err?.message || ''}` },
        ]);
        throw err;
      });
    const showToast = (text) => {
      setToastText(text);
      setToast(true);
      setTimeout(() => setToast(false), 1400);
    };

    // Scoped PATCH — only the slice being edited (do not rewrite siblings)
    let patchFields;
    let changelog;
    if (saveScope === 'goal') {
      patchFields = {
        // The period belongs to the goal, not only to the 目標期間 screen. Sending just
        // the amount left records with no planLength / goalDate at all, so
        // resolveGoalMonthKey returned null and「目標達成に必要」could not be built —
        // while the UI kept showing「6ヶ月プラン」from its own `?? 6` fallback. `months`
        // and `goalDate` are the values this screen already displays and computed above.
        ...kgiPatch,
        ...kgiDrop.patch,
        chartTargets: nextChartTargets,
      };
      changelog = {
        kind: 'goal',
        icon: '🎯',
        title: '最終ゴールを更新',
        by: proposalByLabel,
        summary: droppedNote || whyJoined || undefined,
        changes: [...kgiChanges, ...(kgiDrop.dropped.length ? focusChanges : [])],
      };
    } else if (saveScope === 'subkgi') {
      // 追加KGI only (KGI ②) — 売上 / 期間 untouched. KGI wins over a 注力 holding the same key.
      patchFields = { ...subKgiPatch, ...kgiDrop.patch, chartTargets: nextChartTargets };
      changelog = {
        kind: 'goal',
        icon: '🎯',
        title: '追加KGIを更新',
        by: proposalByLabel,
        summary: droppedNote || undefined,
        changes: [
          ...kgiChanges.filter((c) => c.field === '追加KGI'),
          ...(kgiDrop.dropped.length ? focusChanges : []),
        ],
      };
    } else if (saveScope === 'period') {
      patchFields = {
        planLength: months,
        planStartMonth,
        goalDate,
      };
      changelog = {
        kind: 'goal',
        icon: '🎯',
        title: '目標期間を更新',
        by: proposalByLabel,
        summary: whyJoined || undefined,
        changes: [
          {
            field: '目標期間',
            before: prevPeriodLabel,
            after: periodLabel,
          },
        ],
      };
    } else if (slotN || saveScope === 'kpi') {
      patchFields = { ...focusPatch, chartTargets: nextChartTargets };
      changelog = {
        kind: 'focus',
        icon: '🔁',
        title:
          slotN && removeSlot === slotN
            ? `注力指標${slotLabel(slotN)}を外しました`
            : slotN
              ? `注力指標${slotLabel(slotN)}を更新`
              : '注力指標を更新',
        by: proposalByLabel,
        summary: (slotN ? whys[slotN - 1] : whyJoined) || undefined,
        changes: focusChanges,
      };
    } else if (saveScope === 'actions') {
      // → 確認中; no changelog until a manager approves
      if (!actionsForSubmit.length) return false;
      try {
        const res = await sendActions();
        // 初期設定: the plan is the last step — mark the sequence done in the same tap
        if (setupStep) {
          await setGoals({ ...(goals || {}), setupStep }, { patchFields: { setupStep }, throwOnError: true });
        }
        showToast(
          res?.submission?.status === 'approved' ? '行動プランを保存しました' : '送信しました（マネージャー確認中）'
        );
        setPhase('saved');
        return true;
      } catch (err) {
        if (setupStep) setChatLog((prev) => [...prev, { role: 'ai', text: `保存できませんでした: ${err?.message || ''}` }]);
        return false;
      }
    } else {
      // Full funnel — every field + the goal / 注力 changelog entries
      patchFields = undefined;
      const nextGoals = {
        ...after,
        // phase is computed from planStartMonth now (utils/planPeriod), never stored
        chartTargets: nextChartTargets,
        // no `actions`: 行動 go to 確認中 via submitActions, the live plan stays as approved
      };
      setGoals(nextGoals, {
        changelog: {
          kind: 'goal',
          icon: '🎯',
          title: '目標設定を更新',
          by: proposalByLabel,
          summary: whyJoined || undefined,
          changes: [
            ...kgiChanges,
            {
              field: '目標期間',
              before: prevPeriodLabel,
              after: periodLabel,
            },
          ],
        },
      });
      if (focusChanges.length) {
        appendChangeLogApi({
          kind: 'focus',
          icon: '🔁',
          title: '注力指標を更新',
          by: proposalByLabel,
          summary: whyJoined || undefined,
          changes: focusChanges,
        });
      }
      if (actionsChanged && actionsForSubmit.length) {
        sendActions()
          .then((res) =>
            showToast(
              res?.submission?.status === 'approved'
                ? '目標と行動プランを保存しました'
                : '目標を保存し、行動プランを送信しました（マネージャー確認中）'
            )
          )
          .catch(() => {});
      } else {
        showToast('目標設定を保存しました');
      }
      setRemoveSlot(null);
      setPhase('saved');
      return true;
    }

    // Local optimistic state = previous goals + this slice only (matches API patch)
    if (setupStep) patchFields.setupStep = setupStep;
    const scopedNext = { ...(goals || {}), ...patchFields };
    let saved;
    try {
      // Wait for the server: a refused save (e.g. KGI/注力 conflict) must not read as 保存しました
      saved = await setGoals(scopedNext, { patchFields, changelog, throwOnError: true });
    } catch (err) {
      setChatLog((prev) => [...prev, { role: 'ai', text: `保存できませんでした: ${err?.message || ''}` }]);
      return false;
    }
    // The server records the change as 確認中 for the manager (values are live already).
    showToast(saved?.goalsApprovalStatus === 'pending' ? '保存しました（マネージャーに通知します）' : '目標設定を保存しました');
    setRemoveSlot(null);
    setPhase('saved');
    return true;
  };

  /**
   * ± buttons plus direct entry — reaching ¥1,031,700 from ¥500,000 in ¥10,000
   * steps is 53 taps. Typing shows the raw number (no ¥ / separators) so the
   * field stays editable; it is clamped and reformatted on blur, and a value
   * that is not a number falls back to the last good one.
   */
  const Stepper = ({ value, set, step, min, max, fmtFn }) => {
    const [draft, setDraft] = useState(null);
    const commit = () => {
      if (draft == null) return;
      const n = Number(String(draft).replace(/[^\d.-]/g, ''));
      setDraft(null);
      if (!Number.isFinite(n)) return;
      set(Math.min(max, Math.max(min, Math.round(n))));
    };
    return (
      <div className="stp">
        <button type="button" className="stp-btn" onClick={() => set(Math.max(min, value - step))} disabled={value <= min}>
          −
        </button>
        <input
          className="stp-val"
          type="text"
          inputMode="numeric"
          value={draft ?? fmtFn(value)}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setDraft(String(value))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
          }}
          aria-label="目標値を入力"
        />
        <button type="button" className="stp-btn" onClick={() => set(Math.min(max, value + step))} disabled={value >= max}>
          ＋
        </button>
      </div>
    );
  };

  /** Inline 改善目標 editor for slot n's AI card — same Stepper as the manual picker. */
  const targetEditor = (n) => {
    const metric = metricOf(n);
    const target = targets[n - 1];
    if (!metric?.isSelected || target == null || aiTyping || phase === 'analyzing') return null;
    const unit = metric.unit;
    return (
      <Stepper
        value={Number(target)}
        set={(v) => setTarget(n, v)}
        step={unit === 'currency' ? 100 : 1}
        min={0}
        max={unit === 'percent' ? 100 : unit === 'currency' ? 10000000 : 10000}
        fmtFn={(v) => fmtMetric(unit, v)}
      />
    );
  };

  const dualLabel =
    metrics
      .map((m, i) => (m.isSelected ? `${m.label} ${fmtMetric(m.unit, targets[i])}` : null))
      .filter(Boolean)
      .join(' · ') || '注力指標（未選択）';
  // Goal/period-only must never show 注力 cards — only editTarget==='metric'
  const showMetricCards =
    editTarget === 'metric' &&
    decided &&
    phase !== 'goal_setup' &&
    phase !== 'analyzing' &&
    phase !== 'ask_slot' &&
    phase !== 'resume' &&
    phase !== 'setup_done' &&
    phase !== 'scoped_ready' &&
    phase !== 'saved';
  const scoped = isScopedSlice(scope);
  // 注力-only slices promise「他の項目は変更しません」— a 目標/期間 line there
  // reads as the user having asked for them.
  const kpiOnlyScope = isSlotScope(scope) || scope === 'kpi';
  const scopeSlot = SLOT_OF_SCOPE[scope] || null;
  const activeLabels = activeSlots.map(slotLabel).join('');
  // ②③ may be removed when they hold a KPI now (① is required)
  const canRemoveScopeSlot = scopeSlot > 1 && Boolean(savedKeys[scopeSlot - 1] || metricOf(scopeSlot)?.isSelected);
  const subKgiDef = subKgiKey ? getFocusDef(subKgiKey) : null;
  // 追加KGI target stepper: 指名率 in %, 指名数 in 人 (1 step), seeded from this month's 実績
  const pickSubKgi = (key) => {
    setSubKgiKey(key);
    setSubKgiTarget(key ? Math.round(readFocusActual(key, baseKpis, kpiHistory, baseMonth) ?? 0) : null);
  };
  return (
    <div className="app">
      <NavBar title="目標設定" backLabel="設定" backTo="/settings" hideSettings />
      <ChatBanner icon="🎯" title="目標とプランをAIと設計" subtitle="AIマネージャーが伴走します" />
      <div className="chat-scroll fade">
        {/* Conversation first — the step cards below are the answer to it. */}
        {chatLog.map((m, i) =>
          m.role === 'user' ? (
            <UserBubble key={i} text={m.text} wrap />
          ) : (
            <React.Fragment key={i}>
              {/* text=, not children: children bypass formatSafeChatText, so ** stayed literal
                  and the reply arrived as one unbroken block. */}
              {m.html ? (
                <AiBubble html={m.html} />
              ) : (
                <AiBubble text={m.text || (m.streaming ? '…' : '')} streaming={m.streaming} />
              )}
              {m.choices?.length > 0 && !m.streaming && (
                <div className="chips">
                  {m.choices.map((c) => (
                    <button
                      type="button"
                      key={c}
                      className="chip"
                      disabled={aiTyping}
                      onClick={() => sendSoudan(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          )
        )}
        {!filtersReady && (
          <div className="onb-notice" style={{ marginLeft: 0, maxWidth: '100%' }}>
            店舗・スタイリストが未選択です。ホームで選んでから開き直してください（月はそのまま使えます）。
            <button type="button" className="botl-chip" style={{ marginLeft: 8 }} onClick={() => navigate('/')}>
              ホームへ
            </button>
          </div>
        )}
        {waitingForFirstSnap && (
          <div className="onb-notice" style={{ marginLeft: 0, maxWidth: '100%' }}>
            読み込み中…
          </div>
        )}
        {filtersReady && !waitingForFirstSnap && !posReady && (
          <div className="onb-notice" style={{ marginLeft: 0, maxWidth: '100%' }}>
            POSが空です（{selectedMonth}）。ホームで数値が出る月を選んでください。
          </div>
        )}
        {/* Step intros live in chatLog (see the effect above). The "which item?" prompt is live
            only while the menu is open — once a choice is echoed into the log, the question must
            not linger below the answer. */}
        {phase === 'choose' && <AiBubble html={scopeIntroHtml(scope)} />}

        {phase === 'choose' && (
          <div className="gchoose">
            <button type="button" className="gchoose-btn" onClick={chooseGoal}>
              <span className="gchoose-ic">🎯</span>
              <span className="gchoose-body">
                <span className="gchoose-t">目標売上・期間</span>
                <span className="gchoose-d">KGIと達成期間を見直す</span>
              </span>
              <svg className="gchoose-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="gchoose-btn" onClick={chooseMetric}>
              <span className="gchoose-ic">📊</span>
              <span className="gchoose-body">
                <span className="gchoose-t">注力指標（KPI）</span>
                <span className="gchoose-d">設定中の注力をまとめて見直す（1〜3つ）</span>
              </span>
              <svg className="gchoose-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="gchoose-btn" onClick={chooseActions}>
              <span className="gchoose-ic">✅</span>
              <span className="gchoose-body">
                <span className="gchoose-t">行動プラン</span>
                <span className="gchoose-d">毎日の行動を見直す</span>
              </span>
              <svg className="gchoose-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}

        {editTarget === 'goal' && phase === 'goal_setup' && (
          <>
            <AiBubble
              html={
                scope === 'period'
                  ? '変更後の <strong>目標期間</strong> を選んでください。最終ゴールの金額はそのまま残します。'
                  : scope === 'subkgi'
                    ? funnelStep
                      ? '保存しました。<strong>もう1つKGI</strong>を設定しますか？（任意。売上だけでもOK）<br>KGIに選んだ指標は注力指標には選べません。'
                      : '変更後の <strong>追加KGI</strong> を選んでください（なしにもできます）。売上ゴールと期間はそのまま残します。'
                  : scope === 'goal'
                    ? '変更後の <strong>目標売上（KGI）</strong>・<strong>目標期間</strong> を選んでください。追加KGIはそのまま残します。'
                    : '変更後の <strong>目標売上（KGI）</strong>・<strong>追加KGI</strong>・<strong>目標期間</strong> を選んでください。そこからPOSデータを分析して、注力指標と行動プランを一緒に見直します。'
              }
            />
            <div className="gsetup">
              {scope !== 'period' && scope !== 'subkgi' && (
                <div className="gsetup-block">
                  <div className="gsetup-label">単月売上ゴール（KGI）</div>
                  <Stepper value={sales} set={setSales} step={50000} min={50000} max={3000000} fmtFn={yen} />
                </div>
              )}
              {scope !== 'period' && scope !== 'goal' && (
                // 追加KGI (KGI ②) — its own scope `subkgi` since 2026-09-30; still on the full funnel
                <div className="gsetup-block">
                  <div className="gsetup-label">追加KGI（任意）</div>
                  <div className="echips">
                    {[null, ...SUB_KGI_KEYS].map((k) => (
                      <button
                        key={k || 'none'}
                        type="button"
                        className={`echip${subKgiKey === k ? ' is-on' : ''}`}
                        onClick={() => pickSubKgi(k)}
                      >
                        {k ? getFocusDef(k)?.label : 'なし'}
                      </button>
                    ))}
                  </div>
                  {subKgiDef && (
                    <>
                      <div className="gsetup-deadline">
                        {subKgiDef.label}の目標（{baseLabel} {fmtMetric(subKgiDef.unit, readFocusActual(subKgiKey, baseKpis, kpiHistory, baseMonth))}）
                      </div>
                      <Stepper
                        value={Number(subKgiTarget ?? 0)}
                        set={setSubKgiTarget}
                        step={1}
                        min={0}
                        max={subKgiDef.unit === 'percent' ? 100 : 10000}
                        fmtFn={(v) => fmtMetric(subKgiDef.unit, v)}
                      />
                    </>
                  )}
                  {subKgiKey && draftKeys.includes(subKgiKey) && (
                    // KGI wins — say it before「確認」, not only after the save
                    <div className="gsetup-deadline">
                      今の注力指標に{subKgiDef?.label}が入っています。KGIにすると注力からは自動で外れます。
                    </div>
                  )}
                </div>
              )}
              {/* Shown for the period-only edit too — it was hidden there (copy of the 売上 block's
                  condition), so 「目標期間を変更」 opened with nothing to change */}
              {scope !== 'subkgi' && (
                <div className="gsetup-block">
                  <div className="gsetup-label">目標期間</div>
                  {/* 開始月 (this month unless changed) → 終了月 (年月, up to MAX_PICK_MONTHS ahead) */}
                  <div className="echips gsetup-period">
                    <label className="echip echip--select">
                      開始 {formatMonthLabel(planStartMonth) ?? EM}
                      <select
                        className="echip-select"
                        aria-label="開始月"
                        value={planStartMonth}
                        onChange={(e) => pickStartMonth(e.target.value)}
                      >
                        {startMonthOptions.map((mk) => (
                          <option key={mk} value={mk}>
                            {formatMonthLabel(mk)}
                          </option>
                        ))}
                        {!startMonthOptions.includes(planStartMonth) && (
                          <option value={planStartMonth}>{formatMonthLabel(planStartMonth) ?? planStartMonth}</option>
                        )}
                      </select>
                    </label>
                    <span className="gsetup-period-arrow" aria-hidden="true">→</span>
                    <label className={`echip echip--select${endMonthPickable ? ' is-on' : ''}`}>
                      終了 {deadlineLabel}
                      <select
                        className="echip-select"
                        aria-label="終了月"
                        value={planEndMonth}
                        onChange={(e) => pickEndMonth(e.target.value)}
                      >
                        {endMonthOptions.map((mk) => (
                          <option key={mk} value={mk}>
                            {formatMonthLabel(mk)}
                          </option>
                        ))}
                        {!endMonthPickable && <option value={planEndMonth}>{deadlineLabel}</option>}
                      </select>
                    </label>
                  </div>
                  <div className="gsetup-deadline">
                    {endMonthPickable ? (
                      <>
                        目標期限：<b>{formatGoalDateLabel(deadlineYmd) ?? deadlineLabel}</b>（{months}ヶ月）
                      </>
                    ) : endMonthOptions.length === 0 ? (
                      '開始月が古すぎます。開始月を選び直してください'
                    ) : (
                      `終了月は今月以降、開始月から${MAX_PICK_MONTHS}ヶ月以内で選んでください`
                    )}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="gsetup-go"
                onClick={decideGoal}
                // 追加KGI needs a real target: the server refuses 0 (e.g. 指名数 with no 実績 seeds 0)
                disabled={
                  (scope !== 'period' && Boolean(subKgiKey) && !(Number(subKgiTarget) > 0)) ||
                  (scope !== 'subkgi' && !endMonthPickable)
                }
              >
                {funnelStep ? '次へ →' : isGoalSetupScope(scope) ? 'この内容で確認する →' : 'この目標で相談をはじめる →'}
              </button>
            </div>
            <div className="botl-chips">
              {funnelStep ? (
                <button type="button" className="botl-chip" onClick={() => navigate('/')}>
                  あとで続ける（次回は続きから）
                </button>
              ) : (
                <button type="button" className="botl-chip" onClick={resetGoal}>
                  ← 変更項目を選び直す
                </button>
              )}
            </div>
          </>
        )}

        {decided && phase !== 'goal_setup' && (
          <>
            {(editTarget === 'goal' || editTarget === 'metric') && !kpiOnlyScope && (
              <UserBubble
                text={
                  `目標 ${yen(decided.sales)}` +
                  (subKgiDef ? `・${subKgiDef.label} ${fmtMetric(subKgiDef.unit, subKgiTarget)}` : '') +
                  ` ・ 〜${decided.deadlineLabel}（${decided.months}ヶ月）でお願いします`
                }
                wrap
              />
            )}

            {editTarget === 'metric' &&
              phase !== 'scoped_ready' &&
              phase !== 'saved' &&
              phase !== 'ask_slot' &&
              (phase === 'analyzing' ? (
                <AiBubble>
                  <span className="botl-typing">
                    <span />
                    <span />
                    <span />
                  </span>
                  POSデータを分析中（注力指標{activeLabels}）…
                </AiBubble>
              ) : (
                <AiBubble
                  html={
                    filtersReady
                      ? `目標 <strong>${yen(decided.sales)}</strong> · 残り <strong>約${decided.months}ヶ月</strong>。<br>注力指標<strong>${activeLabels}</strong>の改善目標を提案します。`
                      : `目標 <strong>${yen(decided.sales)}</strong> を受けました。<br>店舗・スタイリストをホームで選んでから開き直してください。`
                  }
                />
              ))}

            {phase === 'analyzing' && canRemoveScopeSlot && (
              // Removing ②③ does not need the model — offer it while the proposal is running
              <div className="botl-chips">
                <button type="button" className="botl-chip" onClick={() => removeFocusSlot(scopeSlot)}>
                  注力指標{slotLabel(scopeSlot)}を外す
                </button>
              </div>
            )}

            {showMetricCards && phase !== 'scoped_ready' && (
              <>
                {/* No「改善目標案」header — the bubble above already says
                    「注力指標①の改善目標を提案します」and the cards follow it directly. */}
                {stanceNote && <AiBubble>{stanceNote}</AiBubble>}
                {activeSlots.map((n) => (
                  <MetricCard
                    key={n}
                    metric={metricOf(n)}
                    target={targets[n - 1]}
                    why={whys[n - 1]}
                    editor={targetEditor(n)}
                  />
                ))}
              </>
            )}

            {phase === 'ask_more' && (
              <>
                <AiBubble
                  html={`注力指標をもう1つ追加しますか？（任意・最大3つ。今は${selectedSlots.length}つ）`}
                />
                <div className="botl-chips">
                  <button type="button" className="botl-chip botl-chip--primary" onClick={addMoreSlot}>
                    追加する
                  </button>
                  <button
                    type="button"
                    className="botl-chip"
                    onClick={() => {
                      logUser('このまま進める');
                      finishMetricStep();
                    }}
                  >
                    このまま進める
                  </button>
                </div>
              </>
            )}

            {phase === 'resume' && (
              <>
                <AiBubble html="前回の続きから始めます。<strong>注力指標①</strong>を決めましょう。" />
                <div className="botl-chips">
                  <button type="button" className="botl-chip botl-chip--primary" onClick={() => startMetricFlow('kpi1')}>
                    注力指標①を決める →
                  </button>
                </div>
              </>
            )}

            {phase === 'setup_done' && (
              <>
                <AiBubble
                  html={
                    selfDecides
                      ? '設定が完了しました 🎉<br>行動プランは確定し、明日から毎日の行動プランになります。<br>目標・注力指標はホームと目標タブに反映されています。'
                      : '設定が完了しました 🎉<br>行動プランはマネージャー確認中です。確定すると毎日の行動プランになります。<br>目標・注力指標はホームと目標タブに反映されています。'
                  }
                />
                <div className="botl-chips">
                  <button type="button" className="botl-chip botl-chip--primary" onClick={() => navigate('/')}>
                    ホームへ
                  </button>
                </div>
              </>
            )}

            {phase === 'ask_slot' && scopeSlot && (
              // First run, ②③: optional — ask before calling the model
              <>
                <AiBubble
                  html={`注力指標<strong>${slotLabel(scopeSlot)}</strong>も設定しますか？（任意です。1つだけでも進められます）`}
                />
                <div className="botl-chips">
                  <button type="button" className="botl-chip botl-chip--primary" onClick={acceptOptionalSlot}>
                    設定する
                  </button>
                  <button type="button" className="botl-chip" onClick={skipOptionalSlots}>
                    設定しない（行動プランへ）
                  </button>
                </div>
              </>
            )}

            {phase === 'metric_proposed' && !manualMetricEditing && (
              <div className="botl-chips">
                <button
                  type="button"
                  className="botl-chip botl-chip--primary"
                  onClick={acceptMetric}
                  disabled={metricAcceptBlocked}
                  title={metricAcceptBlocked ? '改善目標がまだありません' : undefined}
                >
                  {funnelStep ? 'この案で決定 →' : activeSlots.length > 1 ? `この${activeSlots.length}指標で進める` : 'この案で進める'}
                </button>
                <button type="button" className="botl-chip" onClick={recalcSameKpi} disabled={aiTyping}>
                  この指標のまま数値を変える
                </button>
                <button type="button" className="botl-chip" onClick={recalcOtherKpi} disabled={aiTyping}>
                  別の案で再計算
                </button>
                {activeSlots.map((n) => (
                  <button key={n} type="button" className="botl-chip" onClick={() => startManualMetricEdit(n)}>
                    {activeSlots.length > 1 ? `${slotLabel(n)}を自分で選ぶ` : '合わなければ自分で選ぶ'}
                  </button>
                ))}
                {canRemoveScopeSlot && (
                  <button type="button" className="botl-chip" onClick={() => removeFocusSlot(scopeSlot)}>
                    注力指標{slotLabel(scopeSlot)}を外す
                  </button>
                )}
                {addingSlot && !selectedSlots.includes(addingSlot) && (
                  <button type="button" className="botl-chip" onClick={cancelAddSlot}>
                    注力指標{slotLabel(addingSlot)}の追加をやめる
                  </button>
                )}
                {funnelStep ? (
                  <button type="button" className="botl-chip" onClick={() => navigate('/')}>
                    あとで続ける（次回は続きから）
                  </button>
                ) : (
                  <button type="button" className="botl-chip" onClick={resetGoal}>
                    ← 変更項目を選び直す
                  </button>
                )}
              </div>
            )}

            {phase === 'metric_proposed' && manualMetricEditing && (
              <>
                <AiBubble
                  html={`注力指標<strong>${slotLabel(manualMetricSlot)}</strong>を手動で選べます。ホームPOSスナップショットにある指標のみ表示します。KGIの指標と、他の注力で使っている指標は選べません。`}
                />
                <div className="botl-evidence" style={{ marginBottom: 10 }}>
                  <div className="botl-evidence-h">
                    <span className="botl-evidence-name">指標を選択{baseLabel !== '現状' ? `（${baseLabel}）` : ''}</span>
                  </div>
                  <div className="acard-cads" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                    {FOCUS_LEVER_KEYS.map((key) => {
                      const def = FOCUS_KPI_DEFS[key];
                      const actual = readFocusActual(key, baseKpis, kpiHistory, baseMonth);
                      const isKgi = kgiKeys.includes(key);
                      const taken = otherSlotKeys(manualMetricSlot).includes(key);
                      const blocked = isKgi || taken;
                      const on = key === manualPickKey;
                      const actualLabel =
                        formatFocusValue(def.unit, actual) || '未取得';
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`acard-cad${on ? ' is-on' : ''}`}
                          disabled={blocked}
                          title={isKgi ? 'KGIに設定済み' : taken ? '他の注力指標で使っています' : undefined}
                          onClick={() => onManualPickKey(key)}
                          style={{ opacity: blocked ? 0.45 : 1 }}
                        >
                          {def.icon} {def.label}
                          <span style={{ marginLeft: 4, opacity: 0.75 }}>{isKgi ? 'KGI' : actualLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                  {manualPickDef ? (
                    <div className="botl-target" style={{ marginTop: 12 }}>
                      <div className="botl-target-row">
                        <span className="botl-target-k">{baseLabel}</span>
                        <span className="botl-target-v">
                          {formatFocusValue(manualPickDef.unit, manualPickActual) || '未取得'}
                        </span>
                      </div>
                      <div className="botl-target-arrow">↓ 改善目標</div>
                      <div style={{ marginTop: 8 }}>
                        <Stepper
                          value={manualTarget != null ? Number(manualTarget) : 0}
                          set={(v) => setManualTarget(v)}
                          step={manualStep}
                          min={manualMin}
                          max={manualMax}
                          fmtFn={(v) => fmtMetric(manualPickDef.unit, v)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="botl-target" style={{ marginTop: 12, color: 'var(--muted)', fontSize: 12 }}>
                      上の指標を1つ選ぶと、現状値と改善目標案が出ます。
                    </div>
                  )}
                </div>
                <div className="botl-chips">
                  <button
                    type="button"
                    className="botl-chip botl-chip--primary"
                    onClick={applyManualMetricEdit}
                    disabled={!manualPickKey || !canPickKey(manualPickKey, manualMetricSlot)}
                  >
                    この内容にする
                  </button>
                  <button type="button" className="botl-chip" onClick={() => setManualMetricEditing(false)}>
                    キャンセル
                  </button>
                </div>
              </>
            )}

            {phase === 'metric_done' && (
              <AiBubble>
                では、{activeSlots.length > 1 ? `この${activeSlots.length}指標` : 'この注力指標'}を伸ばすための行動プランをご提案します…
              </AiBubble>
            )}

            {(phase === 'actions_proposed' || phase === 'actions_done' || (phase === 'saved' && !scoped)) &&
              actions.length === 0 && (
                <AiBubble
                  text={
                    aiTyping
                      ? '行動プランを考えています…'
                      : '行動プランをまだ受け取れていません。「別の行動プランを提案」でもう一度AIに依頼するか、「手動で編集」で作れます。'
                  }
                  streaming={aiTyping}
                />
              )}

            {(phase === 'actions_proposed' || phase === 'actions_done' || (phase === 'saved' && !scoped)) &&
              actions.length > 0 && (
              <>
                <AiBubble
                  html={`目標売上を達成させるため、<strong>${dualLabel}</strong> を伸ばす毎日の行動として、次の${actions.length}つを提案します。`}
                />
                <div className="botl-actions">
                  {(groupActionsByTarget(actions, actionKeys) || [{ key: null, actions }]).map((g) => (
                    <React.Fragment key={g.key || 'all'}>
                      {g.key && <div className="botl-action-kpi">{actionTargetLabel(g.key, subKgiKey)}</div>}
                      {g.actions.map((a) => {
                        const i = actions.indexOf(a);
                        return (
                          <div className="botl-action" key={a.id || i}>
                            <span className="botl-action-num">{i + 1}</span>
                            <div className="botl-action-body">
                              <div className="botl-action-name">{a.name}</div>
                              <div className="botl-action-cad">{a.cadence}</div>
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            {/* Chips wait for the plan: while the AI is still typing, やる/別の案 read as answers
                to a proposal that is not on screen yet (owner 2026-10-23). */}
            {phase === 'actions_proposed' && !manualEditing && !aiTyping && (
              <div className="botl-chips">
                <button
                  type="button"
                  className="botl-chip botl-chip--primary"
                  onClick={acceptActions}
                  disabled={actions.length === 0 || aiTyping}
                  title={actions.length === 0 ? '行動プランがまだありません' : undefined}
                >
                  やる
                </button>
                <button type="button" className="botl-chip" onClick={proposeAltActions} disabled={aiTyping}>
                  別の行動プランを提案
                </button>
                <button type="button" className="botl-chip" onClick={startManualEdit}>
                  手動で編集
                </button>
                {funnelStep ? (
                  <button type="button" className="botl-chip" onClick={() => navigate('/')}>
                    あとで続ける（次回は続きから）
                  </button>
                ) : (
                  <button type="button" className="botl-chip" onClick={resetGoal}>
                    ← 変更項目を選び直す
                  </button>
                )}
              </div>
            )}

            {phase === 'actions_proposed' && manualEditing && (
              <>
                <AiBubble html="行動プランを直接編集できます。編集が終わったら「この内容にする」を押してください。" />
                <div className="botl-actions">
                  {manualActs.map((a, i) => (
                    <div className="botl-action" key={a.id || i}>
                      <span className="botl-action-num">{i + 1}</span>
                      <div className="botl-action-body" style={{ width: '100%' }}>
                        <input
                          className="acard-name"
                          value={a.name}
                          placeholder="行動の内容を入力"
                          onChange={(e) => setManualActName(i, e.target.value)}
                        />
                        <div className="acard-cads" style={{ marginTop: 6 }}>
                          {MANUAL_CADENCES.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={`acard-cad${a.cadence === c ? ' is-on' : ''}`}
                              onClick={() => setManualActCadence(i, c)}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                        {actionKeys.length > 0 && (
                          <div className="acard-cads" style={{ marginTop: 6 }} aria-label="指標">
                            {[...actionKeys, null].map((k) => (
                              <button
                                key={k || 'none'}
                                type="button"
                                className={`acard-cad${(a.focusKey || null) === k ? ' is-on' : ''}`}
                                onClick={() => setManualActFocus(i, k)}
                              >
                                {k ? actionTargetLabel(k, subKgiKey) : '指標なし'}
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          className="acard-del"
                          style={{ marginTop: 6 }}
                          onClick={() => removeManualAct(i)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="botl-chips">
                  {actionKeys.length >= 2 && (
                    <div className="gsetup-deadline" style={{ width: '100%' }}>
                      指標ごとに1つが目安です
                    </div>
                  )}
                  {manualActs.length < MAX_PLAN_ACTIONS && (
                    <button type="button" className="botl-chip" onClick={addManualAct}>
                      ＋ 行動を追加
                    </button>
                  )}
                  <button type="button" className="botl-chip botl-chip--primary" onClick={applyManualEdit}>
                    この内容にする
                  </button>
                  <button type="button" className="botl-chip" onClick={() => setManualEditing(false)}>
                    キャンセル
                  </button>
                </div>
              </>
            )}

            {(phase === 'scoped_ready' || (phase === 'actions_done' && !scoped) || phase === 'saved') && (
              <>
                {phase === 'scoped_ready' && (
                  <AiBubble html="内容を確認してください。保存すると<strong>この項目だけ</strong>反映されます。" />
                )}
                {phase === 'actions_done' && !scoped && (
                  <>
                    <UserBubble text="この行動プランを採用します" />
                    <AiBubble html={`設定完了です。<br>下の「保存する」を押すと反映されます。`} />
                  </>
                )}
                <div className="botl-summary">
                  <div className="botl-summary-h">✅ 合意内容</div>
                  {(scope === 'goal' || scope === 'period' || !scoped) && (
                    <div className="botl-summary-row">
                      <span>目標 (KGI)</span>
                      <b>
                        {yen(decided.sales)} / 〜{decided.deadlineLabel}（{decided.months}ヶ月）
                      </b>
                    </div>
                  )}
                  {(scope === 'subkgi' || !scoped) && (
                    <div className="botl-summary-row">
                      <span>追加KGI</span>
                      <b>{subKgiDef ? `${subKgiDef.label} ${fmtMetric(subKgiDef.unit, subKgiTarget)}` : 'なし'}</b>
                    </div>
                  )}
                  {scope === 'subkgi' && droppedNote && (
                    <div className="botl-summary-row">
                      <span>注力指標</span>
                      <b>{droppedNote}</b>
                    </div>
                  )}
                  {(kpiOnlyScope || !scoped) &&
                    (scopeSlot ? [scopeSlot] : FOCUS_SLOTS.map((s) => s.slot)).map((n) => {
                      const m = metricOf(n);
                      // Whole-注力 summary lists only the slots that are set; a slot scope always shows
                      if (!scopeSlot && !m.isSelected) return null;
                      return (
                        <div className="botl-summary-row" key={n}>
                          <span>注力{slotLabel(n)}</span>
                          <b>
                            {removeSlot === n
                              ? '外す'
                              : `${m.label} → ${fmtMetric(m.unit, targets[n - 1])}`}
                          </b>
                        </div>
                      );
                    })}
                  {(scope === 'actions' || !scoped) && (
                    <div className="botl-summary-row">
                      <span>行動プラン</span>
                      <b>
                        {actions.length}つの毎日の行動
                        {groupActionsByTarget(actions, actionKeys)
                          ? `（${actionKeys
                              .filter((k) => actions.some((a) => a.focusKey === k))
                              .map((k) => actionTargetLabel(k, subKgiKey))
                              .join('・')}）`
                          : ''}
                      </b>
                    </div>
                  )}
                </div>
                {(phase === 'scoped_ready' || phase === 'actions_done') && (
                  <div className="onb-cta-row">
                    <button type="button" className="onb-cta" onClick={() => save(scope)}>
                      この内容で保存する →
                    </button>
                  </div>
                )}
                {phase === 'saved' && (
                  <>
                    <AiBubble html="保存しました。設定・ホーム・目標タブに反映されます。<br>このままチャットで相談を続けることもできます。" />
                    <div className="botl-chips">
                      {nextFunnelStep(funnelStep) ? (
                        <button
                          type="button"
                          className="botl-chip botl-chip--primary"
                          onClick={goToNextFunnelStep}
                        >
                          {FUNNEL_LABEL[nextFunnelStep(funnelStep)]}
                        </button>
                      ) : !metricOf(1).isSelected && hasSavedGoal ? (
                        // ① is required: e.g. the KGI just took ①'s KPI and nothing was left to move up
                        <button
                          type="button"
                          className="botl-chip botl-chip--primary"
                          onClick={() => {
                            setScope('kpi1');
                            startMetricFlow('kpi1');
                          }}
                        >
                          次へ: 注力指標①を選ぶ
                        </button>
                      ) : (
                        <button type="button" className="botl-chip" onClick={resetGoal}>
                          続けて別の項目を変更する
                        </button>
                      )}
                      {!funnelStep && (isSlotScope(scope) || scope === 'goal') && (
                        // Q4: a new 注力 / 追加KGI may call for a different plan — offered, never forced
                        <button
                          type="button"
                          className="botl-chip"
                          onClick={() => {
                            setScope('actions');
                            setEditTarget('actions');
                            setDecided({ sales, months, deadlineLabel });
                            setActions(goals?.actions?.length ? goals.actions : []);
                            setPhase('actions_proposed');
                            void proposeAltActions();
                          }}
                        >
                          行動プランも見直す（任意）
                        </button>
                      )}
                      <button type="button" className="botl-chip" onClick={() => navigate('/settings')}>
                        設定に戻る
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {aiTyping && !chatLog.some((m) => m.streaming) && (
          <AiBubble>
            <span className="botl-typing">
              <span />
              <span />
              <span />
            </span>
            考え中…
          </AiBubble>
        )}
      </div>

      <ChatInput
        placeholder={
          phase === 'choose' || phase === 'goal_setup'
            ? '目標の相談もできます…(例: 過去の売上と比べてどう？)'
            : '自由に相談…(例: 目標が高すぎる / 根拠は？ / もっと慎重な案)'
        }
        disabled={aiTyping || !filtersReady}
        onSend={sendSoudan}
      />

      <EditToast show={toast} text={toastText} />
    </div>
  );
}
